import ToastEventBus from "primevue/toasteventbus";
import { useUserStore } from "@/stores/userStore";
import { useProfileStore } from "@/stores/profileStore";
import { storeToRefs, getActivePinia } from "pinia";
import Constants from "@/utils/constants";
import EdgeFunction from "@/utils/edgeFunctions";
import { v4 as uuidv4 } from "uuid";
import { Capacitor } from "@capacitor/core";
import moment from "moment";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { config as supabaseConfig } from "../supabaseClient.js";
import _ from "lodash";
import { pdfBase64 } from "pkg-pdf-generator";
import { usePdfLayoutStore } from "@/stores/pdfLayoutsStore";
import { useSignaturesStore } from "@/stores/signaturesStore";
import { useSequenceStore } from "@/stores/sequenceStore";
import { useFormTemplateStore } from "@/stores/formTemplateStore";
import { useFormPageStore } from "@/stores/formPageStore";
import imageCompression from "browser-image-compression";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { toRaw, isReactive } from "vue";
import * as turf from "@turf/turf";
import highwayInventory from "@/assets/data/highway_inventory.json";

import Papa from "papaparse";
import csvRaw from "@/assets/data/merged_highway_vitual_points_data.csv?raw";
import { useBladesStore } from "@/stores/bladesStore";
import { usePermissionsStore } from "@/stores/permissionsStore";
import { useEmailTemplatesStore } from "@/stores/emailTemplatesStore";
import { renderTemplateMessage } from "@/utils/emailTemplate/deliveryGuard";
import { isDraftEmailId } from "@/utils/emailTemplate/draftEmails";
import ExcelJs from "exceljs";
import {
  convertToDisplayableImageFile,
  convertSvgToRasterFile,
  convertSvgUrlToRasterUrl,
  isDisplayableImageType,
  isNonDisplayableImageFile
} from "./imageHelper.js";

function getRemoteStoragePrefix() {
  return `${import.meta.env.VITE_SUPABASE_URL}${import.meta.env.VITE_STORAGE_URL}public`;
}

function getUserFlraAttachmentsBucket(includeBaseStorage = true) {
  return _buildBucketPath(
    getSupabaseFlraAttachmentsBucket(),
    includeBaseStorage
  );
}

function getUsersFormTemplatesAttachmentsBucket(includeBaseStorage = true) {
  return _buildBucketPath(
    getSupabaseFormTemplatesAttachmentsBucket(),
    includeBaseStorage
  );
}

function _buildBucketPath(bucketName, includeBaseStorage) {
  const userId = getCurrentUserId();
  return includeBaseStorage
    ? `/${getSupabaseStorageBucket()}/${bucketName}/${userId}`
    : `${bucketName}/${userId}`;
}

function _resolveValueAtArrayPath(data, pathArr) {
  try {
    if (!Array.isArray(pathArr)) return undefined;
    let cur = data;
    for (const key of pathArr) {
      if (cur == null) return undefined;
      cur = cur?.[key];
    }
    return cur;
  } catch {
    return undefined;
  }
}

function _stringifyPdfValue(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val))
    return val.map(_stringifyPdfValue).filter(Boolean).join(", ");
  if (typeof val === "object") {
    return val?.label ?? val?.attendeeName ?? val?.fileName ?? val?.name ?? "";
  }
  return String(val);
}

function _findListItemLabelById(formData, targetId) {
  try {
    if (!formData || !targetId) {
      console.log(
        "[addParentLabel:findLabel] aborted — empty formData or targetId",
        { targetId }
      );
      return "";
    }
    const stack = [formData];
    let nodesVisited = 0;
    let listArraysSeen = 0;
    const candidateMatches = [];
    while (stack.length) {
      const cur = stack.pop();
      nodesVisited++;
      if (!cur || typeof cur !== "object") continue;

      if (Array.isArray(cur)) {
        for (const v of cur) stack.push(v);
        continue;
      }

      for (const [k, v] of Object.entries(cur)) {
        if (
          typeof k === "string" &&
          k.endsWith("_list_items") &&
          Array.isArray(v)
        ) {
          listArraysSeen++;
          const match = v.find((it) => it?.id === targetId);
          if (match?.label) {
            candidateMatches.push({ listKey: k, label: String(match.label) });
            console.log("[addParentLabel:findLabel] MATCH", {
              targetId,
              listKey: k,
              label: String(match.label),
              nodesVisitedSoFar: nodesVisited,
              listArraysSeenSoFar: listArraysSeen,
              candidateMatchesSoFar: candidateMatches.length
            });
            return String(match.label);
          }
        }

        if (v && typeof v === "object") stack.push(v);
      }
    }
    console.log("[addParentLabel:findLabel] no match", {
      targetId,
      nodesVisited,
      listArraysSeen
    });
    return "";
  } catch (e) {
    console.log("[addParentLabel:findLabel] error", e);
    return "";
  }
}

function _findParentContainerIdForItemId(formData, targetItemId) {
  try {
    if (!formData || !targetItemId) {
      console.log(
        "[addParentLabel:findParent] aborted — empty formData or targetItemId",
        { targetItemId }
      );
      return null;
    }
    const stack = [formData];
    let nodesVisited = 0;
    let listArraysSeen = 0;
    while (stack.length) {
      const cur = stack.pop();
      nodesVisited++;
      if (!cur || typeof cur !== "object") continue;

      if (Array.isArray(cur)) {
        for (const v of cur) stack.push(v);
        continue;
      }

      for (const [k, v] of Object.entries(cur)) {
        if (
          typeof k === "string" &&
          k.endsWith("_list_items") &&
          Array.isArray(v)
        ) {
          listArraysSeen++;
          if (v.some((it) => it?.id === targetItemId)) {
            const parentId = k.slice(0, -"_list_items".length);
            console.log("[addParentLabel:findParent] MATCH", {
              targetItemId,
              listKey: k,
              parentId,
              nodesVisitedSoFar: nodesVisited,
              listArraysSeenSoFar: listArraysSeen
            });
            return parentId;
          }
        }
        if (v && typeof v === "object") stack.push(v);
      }
    }
    console.log("[addParentLabel:findParent] no match — chain ends here", {
      targetItemId,
      nodesVisited,
      listArraysSeen
    });
    return null;
  } catch (e) {
    console.log("[addParentLabel:findParent] error", e);
    return null;
  }
}

/**
 * Path-scoped ancestor walker.
 *
 * Given a leaf path like:
 *   [A, B, C, D, E, "E_list_items", idx, "label"]
 *
 * walks the path itself to derive ancestor labels, instead of doing a global
 * DFS over `formData`. This avoids ambiguity when the same id appears under
 * multiple parents in `formData`, because the path tells us exactly which
 * subtree we're in.
 *
 * Algorithm: for each `i` from `listIdx-1` down to `1`, treat `path[i]` as the
 * child container id and `path[i-1]` as its parent container key, then look
 * up `path[i]`'s label inside `formData[...path[0..i-1]][path[i-1]_list_items]`.
 * Anything that isn't a list container (no matching `_list_items` array, or
 * no matching item) is skipped silently — the walk continues upward until the
 * path is exhausted.
 */
function _getAncestorLabelsViaPath(formData, path) {
  const labels = [];
  // console.log("[addParentLabel:walkPath] START", { path });

  if (!Array.isArray(path) || path.length < 2) {
    // console.log("[addParentLabel:walkPath] aborted — path too short");
    return labels;
  }

  // Find the deepest `_list_items` segment — that anchors the leaf list.
  let listIdx = -1;
  for (let i = path.length - 1; i >= 0; i--) {
    if (typeof path[i] === "string" && path[i].endsWith("_list_items")) {
      listIdx = i;
      break;
    }
  }
  // console.log("[addParentLabel:walkPath] anchor _list_items index in path", {
  //   listIdx,
  //   listKeyAtIndex: listIdx >= 0 ? path[listIdx] : null
  // });
  if (listIdx <= 0) {
    // console.log("[addParentLabel:walkPath] no _list_items in path — nothing to walk");
    return labels;
  }

  for (let i = listIdx - 1; i >= 1; i--) {
    const childContainerId = path[i];
    const parentContainerKey = path[i - 1];
    const parentListPath = [
      ...path.slice(0, i),
      `${parentContainerKey}_list_items`
    ];

    // console.log(`[addParentLabel:walkPath] step (i=${i}) — looking up label`, {
    //   childContainerId,
    //   parentContainerKey,
    //   parentListPath
    // });

    let list = null;
    try {
      list = _resolveValueAtArrayPath(formData, parentListPath);
    } catch (e) {
      // console.log(`[addParentLabel:walkPath] step (i=${i}) — error resolving parent list path`, e);
    }

    if (!Array.isArray(list)) {
      // console.log(`[addParentLabel:walkPath] step (i=${i}) — parent has no _list_items here, skipping`, {
      //   parentContainerKey,
      //   resolvedType: list === null || list === undefined ? "missing" : typeof list
      // });
      continue;
    }

    const item = list.find((it) => it?.id === childContainerId);
    if (item?.label) {
      // console.log(`[addParentLabel:walkPath] step (i=${i}) — MATCH`, {
      //   childContainerId,
      //   parentContainerKey,
      //   label: String(item.label),
      //   siblingsCount: list.length
      // });
      labels.push(String(item.label));
    } else {
      console.log(
        `[addParentLabel:walkPath] step (i=${i}) — no item with that id in parent's list`,
        {
          childContainerId,
          siblingsCount: list.length,
          siblingIds: list.map((it) => it?.id)
        }
      );
    }
  }

  const reversed = labels.reverse();
  // console.log("[addParentLabel:walkPath] END", {
  //   collectedInnerToOuter: [...labels].reverse(),
  //   returningOuterToInner: reversed
  // });
  return reversed;
}

/**
 * Preprocess PDF layouts to support prefix/suffix and concatenation for text blocks.
 *
 * Supported:
 * - { type: "text", value: [..path], prefix: "(", suffix: ")" }
 * - { type: "text", valueParts: [ "Yes ", { path: [..], prefix, suffix }, ... ] }
 *
 * This converts the above into a plain string `value` that `pkg-pdf-generator` can render.
 */
function preprocessPdfLayoutTextValues(layoutJson, formData) {
  const stack = [layoutJson];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur) continue;

    if (Array.isArray(cur)) {
      for (const v of cur) stack.push(v);
      continue;
    }
    if (typeof cur !== "object") continue;

    for (const v of Object.values(cur)) stack.push(v);

    if (cur.type !== "text") continue;

    const hasAffix =
      typeof cur.prefix === "string" || typeof cur.suffix === "string";
    const hasParts = Array.isArray(cur.valueParts);
    const wantsParentLabel = cur.addParentLabel === true;
    if (!hasAffix && !hasParts && !wantsParentLabel) continue;

    let out = "";
    if (hasParts) {
      for (const part of cur.valueParts) {
        if (part == null) continue;
        if (
          typeof part === "string" ||
          typeof part === "number" ||
          typeof part === "boolean"
        ) {
          out += String(part);
          continue;
        }

        const partPath = Array.isArray(part) ? part : part?.path;
        const rawVal = _resolveValueAtArrayPath(formData, partPath);
        const strVal = _stringifyPdfValue(rawVal);
        out += `${part?.prefix ?? ""}${strVal}${part?.suffix ?? ""}`;
      }
    } else if (Array.isArray(cur.value)) {
      const rawVal = _resolveValueAtArrayPath(formData, cur.value);
      const strVal = _stringifyPdfValue(rawVal);
      out = `${cur.prefix ?? ""}${strVal}${cur.suffix ?? ""}`;

      if (wantsParentLabel) {
        console.log(
          "==================== [addParentLabel] BEGIN ===================="
        );
        console.log("[addParentLabel:Step 1] resolve leaf value from path", {
          path: cur.value,
          rawValue: rawVal,
          stringified: strVal,
          baseString: out
        });

        // Step 2: walk ancestors via the path itself (subtree-scoped).
        // Each step uses path[i-1] as the parent container key and looks up
        // path[i]'s label inside `<parent>_list_items` at that scoped depth.
        // This avoids the global-DFS ambiguity when the same id appears under
        // multiple parents elsewhere in `formData`.
        console.log(
          "[addParentLabel:Step 2] walk ancestors via path (subtree-scoped)"
        );
        const ancestorLabels = _getAncestorLabelsViaPath(formData, cur.value);
        console.log(
          "[addParentLabel:Step 2] result — ancestor labels (outer -> inner)",
          ancestorLabels
        );

        // Step 3: join with " > " and prepend.
        if (ancestorLabels.length) {
          const prefix = ancestorLabels.join(" > ");
          const before = out;
          out = out ? `${prefix} > ${out}` : prefix;
          console.log("[addParentLabel:Step 3] join + prepend", {
            joinedPrefix: prefix,
            valueBefore: before,
            valueAfter: out
          });
        } else {
          console.log(
            "[addParentLabel:Step 3] SKIPPED — no ancestor labels collected"
          );
        }
        console.log(
          "[addParentLabel:Step 4] final value (about to write to cur.value)",
          out
        );
      }
    }

    cur.value = out;
    delete cur.valueParts;
    delete cur.prefix;
    delete cur.suffix;
    delete cur.addParentLabel;
    if (wantsParentLabel) {
      console.log(
        "[addParentLabel:Step 5] node after mutation (what pkg-pdf-generator will see)",
        {
          type: cur.type,
          style: cur.style,
          value: cur.value,
          valueType: typeof cur.value,
          valueLength: typeof cur.value === "string" ? cur.value.length : null
        }
      );
      console.log(
        "==================== [addParentLabel] END ======================"
      );
    }
  }

  return layoutJson;
}

function getSupabaseFlraAttachmentsBucket() {
  return supabaseConfig.supabaseFlraAttachmentsBucket;
}

function getSupabaseFormTemplatesAttachmentsBucket() {
  return supabaseConfig.supabaseFormTemplatesAttachmentsBucket;
}

function getSupabaseStorageBucket() {
  return supabaseConfig.supabaseStorageBucket;
}

function getRandomUuid() {
  return uuidv4();
}

function getBaseUrl() {
  const protocol = window.location.protocol;
  const currentDomain = window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : "";
  return `${protocol}//${currentDomain}${port}`;
}

function resetPiniaStore() {
  // map through that list and use the **$reset** fn to reset the state
  getActivePinia()._s.forEach((store) => store.$reset());
}

function parseHashUrl(hashData) {
  let hash = deepCopy(hashData);

  // Remove the leading '#' if present
  if (hash.startsWith("#")) {
    hash = hash.substring(1);
  }

  // Split the hash string into key-value pairs
  const pairs = hash.split("&");
  const result = {};

  // Iterate over each pair
  pairs.forEach((pair) => {
    const [key, value] = pair.split("=");
    result[key] = decodeURIComponent(value);
  });

  return result;
}

function showToast(summary, detail, severity = "success", life = 3000) {
  ToastEventBus.emit("add", {
    severity,
    summary,
    detail,
    life
  });
}

function convertToLocalDate(date, format) {
  try {
    format = format || "DD/MM/YY";
    return moment(date).local().format(format); // `.local()` converts to the local timezone
  } catch (error) {
    return null;
  }
}

function unixToTimeStamp(expires_at) {
  return {
    timestamp: moment.unix(expires_at).local().toDate(),
    local: moment.unix(expires_at).local().format("DD/MM/YYYY HH:mm A")
  };
}

const getManagerEmail = async (id) => {
  let response = await EdgeFunction.getManagerInfo(id);

  if (response.error) {
    throw response.error;
  }
  return response.data.data.mail;
};

function isSessionExpiringSoon() {
  const { session } = storeToRefs(useUserStore());

  // Get the expires_at value, defaulting to null if it's undefined or empty
  const { expires_at } = session.value;

  // Get the current time
  const currentTime = moment();

  // Default values in case expires_at is null or undefined
  if (!expires_at) {
    return {
      isExpired: true,
      isExpiringSoon: false,
      local: currentTime.format("DD/MM/YYYY hh:mm A")
    };
  }

  // Get the expiration time as a moment object
  const expirationTime = moment.unix(expires_at).local();

  // Calculate the "about to expire" threshold (1 day before expiration)
  const expiringSoonThreshold = expirationTime.clone().subtract(1, "days");

  // Compare the expiration time with the current time
  return {
    isExpired: currentTime.isAfter(expirationTime),
    isExpiringSoon: currentTime.isBetween(
      expiringSoonThreshold,
      expirationTime
    ),
    local: expirationTime.format("DD/MM/YYYY hh:mm A")
  };
}

function getTokenAge() {
  try {
    const { session } = storeToRefs(useUserStore());

    // Return 0 if no session or expires_at
    if (!session?.value?.expires_at) {
      return 0;
    }

    const { expires_at } = session.value;
    const expirationTime = moment.unix(expires_at);
    const currentTime = moment();

    // Calculate hours until expiry
    const hoursUntilExpiry = Math.floor(
      expirationTime.diff(currentTime, "hours")
    );
    // if we need mins then uncomment this
    // const minsUntilExpiry = Math.floor(
    //   expirationTime.diff(currentTime, "minutes")
    // );

    return hoursUntilExpiry;
  } catch (error) {
    console.error("Error calculating token age:", error);
    return 0;
  }
}

function getDummyObj(length) {
  return Array.from({ length }, () => ({}));
}

/**
 * convert the date to a readable format in America/Edmonton timezone (MST / MDT) utilizing the default JavaScript Date functions
 * @param {String} utcDateString string in UTC to be formatted
 * @param {?Enum} monthFormat optional way to format the month to a supported format as per the DATE_TIME_FORMAT enum in Constants
 * @param {boolean} timeNeeded if the time is needed in the return, or just the month/day for formatting
 * @returns
 */
function convertToLocalDateTime(
  utcDateString,
  monthFormat,
  config = {
    timeNeeded: true,
    hour12Clock: true
  }
) {
  const date = new Date(utcDateString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date string");
  }
  //default to numeric 2 digit styling for dates
  let month = "2-digit";
  if (monthFormat) {
    month = monthFormat;
  }

  if (config.timeNeeded) {
    // Format the date and time to local timezone
    return date.toLocaleString("en-US", {
      timeZone: "America/Edmonton",
      year: "numeric",
      month: month,
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: config.hour12Clock // Adjust to false if you prefer 24-hour format
    });
  } else {
    return date.toLocaleString("en-US", {
      timeZone: "America/Edmonton",
      year: "numeric",
      month: month,
      day: "2-digit"
    });
  }
}

function convertToLocalTime(utcDateString) {
  const date = new Date(utcDateString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date string");
  }

  return date.toLocaleString("en-US", {
    timeZone: "America/Edmonton",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
}

function convertToLocalTime24(utcDateString) {
  const date = new Date(utcDateString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date string");
  }

  return date.toLocaleTimeString("en-US", {
    timeZone: "America/Edmonton",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  });
}

function deepCopy(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error("ERROR in deepCopy :", error);
    return null;
  }
}

/**
 * Deeply replaces placeholder tokens inside an object/array tree.
 *
 * - Replaces string values only (keys are left unchanged)
 * - Works well for JSON-like layout definitions (pdf layouts, trigger configs, etc.)
 * - Additive utility: does not mutate the input value
 */
function replaceTokensDeep(value, replacements) {
  if (value == null) return value;

  // Fast path: invalid or empty replacements
  if (!replacements || typeof replacements !== "object") return value;

  if (Array.isArray(value)) {
    return value.map((v) => replaceTokensDeep(v, replacements));
  }

  if (typeof value === "string") {
    let out = value;
    for (const [token, replacement] of Object.entries(replacements)) {
      if (!token) continue;
      if (replacement == null) continue;

      const replacementStr = String(replacement);

      // 1) Special-case: support `${TOKEN}_list_items` -> `${replacement}_list_items`
      // This allows layout paths like `PARENT_ITEM_ID_list_items` to become `<uuid>_list_items`.
      //
      // Important: avoid replacing `ITEM_ID_list_items` inside `PARENT_ITEM_ID_list_items`.
      // Treat underscore as part of an identifier and only match when the token is a
      // standalone segment (not adjacent to [A-Za-z0-9_]).
      const safeTokenListItems = String(`${token}_list_items`).replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );
      out = out.replace(
        new RegExp(
          `(?<![A-Za-z0-9_])${safeTokenListItems}(?![A-Za-z0-9_])`,
          "g"
        ),
        `${replacementStr}_list_items`
      );

      // 2) Replace standalone token values only.
      // Use word-boundaries so `ITEM_ID` does NOT replace inside `PARENT_ITEM_ID`.
      const safeToken = String(token).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      out = out.replace(new RegExp(`\\b${safeToken}\\b`, "g"), replacementStr);
    }
    return out;
  }

  if (typeof value !== "object") return value;

  const out = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = replaceTokensDeep(v, replacements);
  }
  return out;
}

// Checks if value exists (not null/undefined)
function isValueExist(val) {
  return val != null && val != undefined;
}
function isStringValueExist(val) {
  return val != null && val != undefined && val != "";
}

/**
 * Trims a string and returns NULL when nothing is left.
 *
 * For writing optional text to a nullable column. An untouched input arrives as
 * `""`, and storing that gives the column two ways to say "no value" — `''` and
 * NULL — which every later reader then has to test for. NULL is the valid one,
 * so it is settled on the way out rather than re-derived at each read.
 *
 * Non-strings pass through untouched, so it is safe on a value that may already
 * be null. Text and uuid params only: the emptiness test is
 * `isStringValueExist`, which treats `0` as absent, so do not route numbers
 * through it.
 *
 * @param {*} value
 * @returns {*} the trimmed string, the original non-string, or null
 */
function blankToNull(value) {
  const trimmed = typeof value === "string" ? value.trim() : value;
  return isStringValueExist(trimmed) ? trimmed : null;
}

/**
 * The readable text of an HTML string: tags become spaces, whitespace is
 * collapsed and trimmed. Non-strings give "".
 */
function stripHtml(value) {
  return typeof value === "string"
    ? value
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";
}

function formDataValidation(formData) {
  // Loop through all the keys in the formData object
  for (let key in formData) {
    // If any value is not true, return false
    if (formData[key] !== true) {
      return false;
    }
  }
  // If all values are true, return true
  return true;
}

function getEmpId() {
  const { userData } = storeToRefs(useUserStore());
  const { emp_id } = userData.value;
  return emp_id || "XXX";
}

function getUserName() {
  const { userData } = storeToRefs(useUserStore());
  const { user_metadata } = userData.value;
  if (!user_metadata) return null;
  const { full_name } = user_metadata;
  if (!full_name) return userData.value.full_name;
  return full_name;
}

function getUserEmail() {
  const { userData } = storeToRefs(useUserStore());
  const { user_metadata } = userData.value;
  if (!user_metadata) return null;
  const { email } = user_metadata;
  if (!email) return userData.value.email;
  return email;
}

function getInitials() {
  const { userData } = storeToRefs(useUserStore());
  const { user_metadata } = userData.value;
  if (!user_metadata) return null;
  let { full_name } = user_metadata;

  if (!full_name) {
    full_name = userData.value.full_name;
  }

  const nameParts = full_name.split(" ");
  const initials = nameParts
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2); // Get first character of each part and limit to 2
  return initials.join(""); // Join the initials
}

const getCurrentUserOid = () => {
  const { userData } = storeToRefs(useUserStore());
  return userData.value?.user_metadata?.custom_claims?.oid || "";
};

function getCurrentUserId() {
  const { userData } = storeToRefs(useUserStore());
  const oid = getCurrentUserOid();
  if (isStringValueExist(oid)) {
    return oid;
  }
  // If oid is empty, return the default id
  return userData.value?.id || "";
}

function getCurrentUserEmail() {
  const { userData } = storeToRefs(useUserStore());
  const { email } = userData.value;
  return email || "";
}

function getCurrentUserFullName() {
  const { userData } = storeToRefs(useUserStore());
  const { user_metadata } = userData.value;
  return user_metadata?.full_name || userData.value.full_name;
}

function getCurrentEnvironment() {
  return Constants.enums.PROJECT_ENVIRONMENT.CURRENT.toLowerCase();
}

function isCurrentEnvironmentProd() {
  const currentEnv = getCurrentEnvironment();
  const prodEnv = Constants.enums.PROJECT_ENVIRONMENT.PRODUCTION.toLowerCase();
  return currentEnv == prodEnv;
}

function isCurrentEnvironmentDev() {
  const currentEnv = getCurrentEnvironment();
  const devEnv = Constants.enums.PROJECT_ENVIRONMENT.DEV.toLowerCase();
  return currentEnv == devEnv;
}

function isCurrentEnvironmentStaging() {
  const currentEnv = getCurrentEnvironment();
  const stagingEnv = Constants.enums.PROJECT_ENVIRONMENT.STAGING.toLowerCase();
  return currentEnv == stagingEnv;
}

function getHseEmail(company) {
  // If it's not prod then retun current user email instead of HSE
  if (!isCurrentEnvironmentProd()) {
    return getCurrentUserEmail();
  }
  const companyLowerCase = company.toLowerCase(); // Convert company to lowercase
  switch (companyLowerCase) {
    case Constants.enums.FLRA_COMPANIES.CRANE:
      return Constants.enums.HSE_EMAILS.CRANE;
    case Constants.enums.FLRA_COMPANIES.ENT:
      return Constants.enums.HSE_EMAILS.ENT;
    case Constants.enums.FLRA_COMPANIES.CAP:
      return Constants.enums.HSE_EMAILS.CAP;
    default:
      return null;
  }
}

function getCurrentUserRoles() {
  const { userData } = storeToRefs(useUserStore());
  const { roles } = userData.value;
  return roles || [];
}

function hasRole(roleId) {
  const roles = getCurrentUserRoles();
  return roles.includes(roleId);
}

function isUserSubmitter() {
  return hasRole(Constants.enums.FLRA_USER_ROLES.SUBMITTER);
}

function isUserSupervisor() {
  return hasRole(Constants.enums.FLRA_USER_ROLES.SUPERVISOR);
}

function isUserAdmin() {
  return (
    hasRole(Constants.enums.FLRA_USER_ROLES.ADMIN) ||
    hasRole(Constants.enums.FLRA_USER_ROLES.SUPER_ADMIN)
  );
}

function isUserSuperAdmin() {
  return hasRole(Constants.enums.FLRA_USER_ROLES.SUPER_ADMIN);
}

function isUserShippingReqAdmin() {
  return hasRole(Constants.enums.FLRA_USER_ROLES.SHIPPING_ADMIN);
}

function openLinkInNewTab(url) {
  window.open(url, "_blank");
}

function getStatusColor(status) {
  switch (status) {
    case Constants.enums.FLRA_STATUS.CANCELLED:
      return "danger";
    case Constants.enums.FLRA_STATUS.NEW:
      return "info";
    case Constants.enums.FLRA_STATUS.OPEN:
      return "info";
    case Constants.enums.FLRA_STATUS.PENDING_REVIEW:
      return "warn";
    case Constants.enums.FLRA_STATUS.REVIEWED:
      return "success";
    case Constants.enums.FLRA_STATUS.DONE:
      return "contrast";
    default:
      return "secondary";
  }
}

function getQCProgressStatusColor(status) {
  switch (status) {
    case Constants.enums.PROGRESS_STATUS.IN_PROGRESS:
      return "primary";
    case Constants.enums.PROGRESS_STATUS.COMPLETE:
      return "success";
    case Constants.enums.PROGRESS_STATUS.NOT_STARTED:
      return "danger";
    case Constants.enums.PROGRESS_STATUS.UNKNOWN:
      return "info";
    default:
      return "secondary";
  }
}

function getDefectsProgressStatusColor(status) {
  const group = getGroupByStatus(status);
  if (group) {
    return group.progressColor;
  }
  return "secondary"; // default fallback
}

const getStatusClasses = (status) => {
  const group = getGroupByStatus(status);
  if (group) {
    return group.cssClasses;
  }
  return "bg-gray-100 text-gray-700"; // default fallback
};

const getStatusClassesReversed = (status) => {
  const group = getGroupByStatus(status);
  if (group) {
    // Reverse the bg and text colors from the original cssClasses
    const cssClasses = group.cssClasses;
    // Replace text-{color}-{intensity} bg-{color}-{intensity} with bg-{color}-{intensity} text-{color}-{intensity}
    return cssClasses.replace(
      /text-(\w+)-(\d+)\s+bg-(\w+)-(\d+)/g,
      "bg-$1-$2 text-$3-$4"
    );
  }
  return "bg-gray-700 text-gray-100"; // default fallback
};

const getDefectsStatusIconClass = (status) => {
  const group = getGroupByStatus(status);
  if (group && group.individualStatuses && group.individualStatuses[status]) {
    return group.individualStatuses[status].iconClass;
  }
  return "pi pi-circle"; // default fallback
};

const getStatusGroupClasses = (statusKey) => {
  const group =
    Constants.enums.DEFECT_STATUS_GROUPS[statusKey] ||
    Constants.enums.EMERGENCY_STATUS_GROUPS[statusKey];
  return group ? group.cssClasses : "text-gray-800 bg-gray-50";
};

const getStatusGroupProgressColor = (statusKey) => {
  const group = Constants.enums.DEFECT_STATUS_GROUPS[statusKey];
  return group ? group.progressColor : "secondary";
};

const getGroupByKey = (key) => Constants.enums.DEFECT_STATUS_GROUPS[key];

const getGroupByStatus = (status) => {
  for (const [groupKey, group] of Object.entries(
    Constants.enums.DEFECT_STATUS_GROUPS
  )) {
    if (group.statuses.includes(status)) {
      return group;
    }
  }
  return null;
};

const getStatusGroupTooltip = (statusKey, statusItemsStructure) => {
  const group =
    Constants.enums.DEFECT_STATUS_GROUPS[statusKey] ||
    Constants.enums.EMERGENCY_STATUS_GROUPS[statusKey];
  if (group) {
    const statusGroupName =
      statusItemsStructure.find((item) => item.key === statusKey)?.label ||
      group.label;
    const statusList = group.statuses
      .map((s) => s.replace(/_/g, " "))
      .join("\n- ");
    return `${statusGroupName}\n\nIncludes:\n- ${statusList}`;
  }
  return "";
};

const getStatusItemsStructure = () => {
  return Object.values(Constants.enums.DEFECT_STATUS_GROUPS).map((group) => ({
    key: group.key,
    label: group.label,
    count: 0,
    prefix: group.prefix
  }));
};

const getEmergencyStatusItemsStructure = () => {
  return Object.values(Constants.enums.EMERGENCY_STATUS_GROUPS).map(
    (group) => ({
      key: group.key,
      label: group.label,
      count: 0,
      prefix: group.prefix
    })
  );
};

const getStatusGroups = () => {
  const groups = {};
  Object.entries(Constants.enums.DEFECT_STATUS_GROUPS).forEach(
    ([key, group]) => {
      groups[key] = group.statuses;
    }
  );
  return groups;
};

const getFilterOptions = () => {
  return Object.values(Constants.enums.DEFECT_STATUS_GROUPS).map((group) => ({
    label: group.label,
    status: group.key,
    items: group.statuses
      .filter((status) => status !== "NEW")
      .map((status) => {
        // Create a meaningful label for each status
        let label = status;

        if (status === "SUBMITTED") {
          status = [status, "NEW"];
        }

        return {
          label: label,
          filter: status
        };
      })
  }));
};

function getStatusName(statusId) {
  switch (statusId) {
    case 1:
      return Constants.enums.FLRA_STATUS.NEW;
    case 2:
      return Constants.enums.FLRA_STATUS.OPEN;
    case 3:
      return Constants.enums.FLRA_STATUS.PENDING_REVIEW;
    case 4:
      return Constants.enums.FLRA_STATUS.REVIEWED;
    case 5:
      return Constants.enums.FLRA_STATUS.DONE;
    case 6:
      return Constants.enums.FLRA_STATUS.CANCELLED;
    default:
      return Constants.enums.FLRA_STATUS.NEW;
  }
}

function getBase64FromBlob(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob); // Read the Blob as Data URL
  });
}
function createBase64FromBuffer(contentType, bufferData) {
  // Convert buffer data to a Base64 string
  const base64String = btoa(
    new Uint8Array(bufferData).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      ""
    )
  );

  // Prepend the data URL prefix with the content type
  const base64Url = `data:${contentType};base64,${base64String}`;

  return base64Url;
}

function base64ToBlobUrl(base64, contentType = "", sliceSize = 512) {
  const byteCharacters = atob(base64.split(",")[1]);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  // Create a Blob URL from the Blob
  const blobUrl = URL.createObjectURL(blob);

  return blobUrl;
  // return blob;
}

function createBlobUrlFromBuffer(contentType, bufferData) {
  // Create a Blob from the buffer data and content type
  const blob = new Blob([bufferData], { type: contentType });

  // Generate a URL for the Blob
  const url = URL.createObjectURL(blob);

  return url;
}

function getCurrentVersion() {
  return process.env.VUE_APP_VERSION || "N/A";
}

function isContainsAlphabet(str) {
  const alphabetRegex = /[a-zA-Z]/;
  return alphabetRegex.test(str);
}

// Function to load the SVG and convert it to Base64
const getBytesOfSvg = async (url) => {
  const response = await fetch(url);
  const text = await response.text();
  const base64 = btoa(unescape(encodeURIComponent(text))); // Convert to Base64
  return atob(base64); // Prefix with the proper data type
};

// Function to format the date as 'YYYYMMDD_HHMMSS'
const formatTimestamp = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

const isImage = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith("image/")) return true;
  // On Windows, HEIC/HEIF often have wrong or empty file.type; treat by extension so they go through image path (crop + conversion)
  const name =
    file.name && typeof file.name === "string" ? file.name.toLowerCase() : "";
  return /\.(heic|heif)$/.test(name);
};
const isPdf = (file) => {
  return file && file.type.includes("/pdf");
};
const isHTML = (file) => {
  return file && file.type.includes("html");
};

const isNativePlatForm = () => {
  return Capacitor.isNativePlatform();
};

function sanitizeFilename(filename) {
  // Replace all '/' with '_'
  return `${filename.replace(/\//g, "_")}`;
}

function getFileExtension(filename) {
  // Match the file extension using a regex and return it
  const match = filename.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1] : null; // Return the extension or null if not found
}

async function convertImageToBase64(url) {
  try {
    const userStore = useUserStore();
    if (!userStore.isOnline) {
      // Generate a white background base64 string
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size (e.g., 300x300 pixels)
      canvas.width = 300;
      canvas.height = 300;

      // Fill canvas with white background
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Return only the base64 byte string without the prefix
      const base64Data = canvas.toDataURL("image/png");
      return base64Data;
    }

    // Fetch the image from the URL
    const response = await fetch(url);
    if (!response.ok) {
      showToast("Error", "Failed to fetch image data.", "error");
      return null;
    }

    const blob = await response.blob();

    // Read the blob as a base64 encoded string
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob); // Converts to data URL format
    });
  } catch (error) {
    return null;
  }
}

const automatedMessagePlaceHolder = (recipients) => {
  // Ensure only unique recipients
  const uniqueRecipients = [...new Set(recipients)];
  return (
    `<br/>` +
    `<p>This is an automated message. Please do not reply to this email.</p>` +
    `<p style="margin-top: 10px; font-size: 11px; color: #888;">DEV MODE: Original Recipients: ${uniqueRecipients.join(", ")}</p>`
  );
};

// Helper function to generate email body
const generateFLRAEmailBody = (formId, msg, isMeetingStandard) => {
  const formLink = `${getBaseUrl()}${Constants.enums.ROUTE_ENUM.FLRA_FORM}/${formId}`;
  return `
    <p>Hello,</p>
    ${isValueExist(isMeetingStandard) ? (isMeetingStandard ? `<p>Your FLRA meets the standard.</p>` : `<p>Your FLRA does not meet the standard.</p>`) : ""}
    <p>${msg}</p> 
    <a href="${formLink}" target="_blank">Form Link</a> 
    <p>Thank you.</p>`;
};

// Helper function to generate access url for shipping requests
const getShippingReqUrl = (formId) => {
  return `${getBaseUrl()}${Constants.enums.ROUTE_ENUM.SHIPPING_REQUEST_FORM}/${formId}`;
};

const objToStringify = (arr) => {
  return JSON.stringify(arr);
};

function getMediaTypeFromBase64(base64) {
  if (!base64) throw "Please provide valid base64";
  let _temp = base64.split("data:")[1];
  return _temp.split(";")[0];
}
function getExtensionFromBase64(base64) {
  if (!base64) throw new Error("Please provide a valid base64 string");

  const mediaTypeSegment = base64.split("data:")[1];
  if (!mediaTypeSegment) throw new Error("Invalid base64 format");

  const mimeType = mediaTypeSegment.split(";")[0];
  const extension = mimeType.split("/")[1];

  if (!extension) throw new Error("Could not determine file extension");

  return extension;
}

function getIndexedDBVersion(databaseName) {
  return new Promise(function (resolve, reject) {
    var request = window.indexedDB.open(databaseName);

    request.onsuccess = function (event) {
      var db = event.target.result;
      var version = db.version; // Get the current version
      db.close(); // Always close the database after accessing metadata
      resolve(version);
    };

    request.onerror = function (event) {
      reject(event.target.error);
    };
  });
}

async function downloadToLocal(base64Data, attachName) {
  const url = base64ToBlobUrl(base64Data, getMediaTypeFromBase64(base64Data));
  const link = document.createElement("a");
  link.href = url;
  link.download = attachName;
  if (isNativePlatForm()) {
    // Handle PDF generation for iOS and Android
    await savePdfToFilesystem(base64Data, attachName);
  } else {
    try {
      // Attempt to trigger the download
      link.click();
    } catch (error) {
      // Fallback: Open the URL in the current tab if download fails
      window.location.href = url;
    }
  }
}

const isMobileBrowser = () => {
  const hasTouchScreen =
    navigator.maxTouchPoints > 0 || "ontouchstart" in window;
  const isSmallScreen = window.innerWidth <= 1024;
  const userAgentCheck =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  const userAgentDataCheck = navigator.userAgentData?.mobile || false;
  const hasOrientationSupport =
    "orientation" in window || window.screen.orientation;

  return (
    userAgentDataCheck || // latest API confirms a mobile device
    (hasTouchScreen && isSmallScreen) || // Touch support + small screen
    (userAgentCheck && hasTouchScreen) || // User Agent + touch support
    (hasOrientationSupport && isSmallScreen) // Orientation support + small screen
  );
};

/**
 * Compresses an image file using browser-image-compression library.
 * Applies compression based on file size with progressive quality reduction.
 */
async function compressAndRotateFile(
  imageFile,
  mimeType,
  thresholdSize = 10000
) {
  console.log(
    "Original file size:",
    getAttachmentSizeInMB(imageFile.size),
    "MB"
  );
  console.log("originalFile instanceof Blob", imageFile instanceof Blob);

  try {
    // Determine compression options based on file size
    const fileSizeMB = getAttachmentSizeInMB(imageFile.size);
    let maxSizeMB = 1; // Default max size

    // Set reasonable bounds for very large or small files
    if (fileSizeMB > 5) {
      maxSizeMB = fileSizeMB * 0.7; // 30% compression for large files
    } else if (fileSizeMB > 1) {
      maxSizeMB = fileSizeMB * 0.75; // 25% compression for large files
    } else if (fileSizeMB < 1) {
      maxSizeMB = fileSizeMB * 0.8; // Minimum 80% compression for small files
    } else {
      maxSizeMB = fileSizeMB * 0.8; // Minimum 80% compression for small files
    }

    const options = {
      maxSizeMB: maxSizeMB,
      useWebWorker: true
    };

    const compressedFile = await imageCompression(imageFile, options);

    const compressionRatio = (
      ((imageFile.size - compressedFile.size) / imageFile.size) *
      100
    ).toFixed(2);
    console.log("Compression ratio:", compressionRatio + "%");

    return compressedFile;
  } catch (error) {
    console.error("Compression failed:", error);
    console.log("Returning original file due to compression error");
    return imageFile;
  }
}

function getAttachmentSizeInMB(size) {
  return size / 1024 / 1024;
}

function getBoldTxt(txt) {
  return `<b> ${txt} </b>`;
}

function findDynamicKeyInExpression(
  expression,
  response,
  page,
  multipleFieldInfo = null
) {
  if (!isValueExist(expression)) {
    return "";
  }

  if (expression === Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_NAME) {
    return getUserName();
  }

  if (typeof expression !== "string") {
    const stringValue = String(expression);
    if (stringValue) {
      expression = stringValue;
    } else {
      return expression;
    }
  }

  // Handle multiple field context by merging current record data into the response
  let workingResponse = response;
  if (
    multipleFieldInfo &&
    multipleFieldInfo.isMultiple &&
    multipleFieldInfo.multipleParentId &&
    multipleFieldInfo.recordIndex >= 0
  ) {
    const parentVal = findAndSetKeyInObject(
      response,
      multipleFieldInfo.multipleParentId
    );
    const currentRecord = parentVal[multipleFieldInfo.recordIndex];

    // Create a new response object with current record data merged in
    workingResponse = {
      ...response,
      ...currentRecord
    };
  }

  // Handle Handlebars-style array looping: {{#each $(arrayKey).property}}...{{/each}}
  // this is an old implmentation kept for backward compatibility, use new implementation below
  expression = expression.replace(
    /\{\{#each\s+\$\(([^)]+)\)(?:\.([^}]+))?\}\}([\s\S]*?)\{\{\/each\}\}/g,
    (match, id, propertyPath, innerTemplate) => {
      const val = findAndSetKeyInObject(workingResponse, id);
      let arr = val;

      if (propertyPath) {
        // Handle nested property access
        const properties = propertyPath.split(".");
        arr = properties.reduce((current, prop) => current?.[prop], val) || [];
      }

      if (!Array.isArray(arr)) return "";

      return arr
        .map((item, index) => {
          // Process the template with Handlebars-style {{this.property}} and {{this}} syntax
          return innerTemplate
            .replace(/\{\{this\.([^}]+)\}\}/g, (_, propertyPath) => {
              const properties = propertyPath.split(".");
              return (
                properties.reduce((current, prop) => current?.[prop], item) ||
                ""
              );
            })
            .replace(/\{\{this\}\}/g, item || "");
        })
        .join("");
    }
  );

  // Handle Handlebars-style {{this.property}} and {{this}} syntax outside of loops
  // This is needed for multiple field contexts where the current record data is merged into the response
  expression = expression.replace(
    /\{\{this\.([^}]+)\}\}/g,
    (_, propertyPath) => {
      const properties = propertyPath.split(".");
      return (
        properties.reduce(
          (current, prop) => current?.[prop],
          workingResponse
        ) || ""
      );
    }
  );
  expression = expression.replace(/\{\{this\}\}/g, workingResponse || "");

  // Handle conditional rendering: {{#if $(field-id)}}...{{/if}}
  // {{#if $(8e405e79-89d6-4ccf-b670-c97fb7b771ba)}}<span>Pickup By: $(8e405e79-89d6-4ccf-b670-c97fb7b771ba)<br/></span>{{/if}}
  expression = expression.replace(
    /\{\{#if\s+\$\(([^)]+)\)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (match, fieldId, content) => {
      const fieldValue = findAndSetKeyInObject(workingResponse, fieldId);
      return fieldValue ? content : "";
    }
  );

  // Handle array looping with template syntax: $(arrayKey).loop(template)
  // Process all loop expressions by finding them and replacing them one by one
  let loopMatch;
  const loopRegex = /\$\(([^)\[\]]+)\)\.loop\(/g;

  while ((loopMatch = loopRegex.exec(expression)) !== null) {
    const key = loopMatch[1];
    const val = findAndSetKeyInObject(workingResponse, key);

    if (Array.isArray(val) && val.length > 0) {
      // Find the matching closing parenthesis by counting parentheses
      const startIndex = loopMatch.index + loopMatch[0].length;
      let parenCount = 1;
      let endIndex = startIndex;

      while (endIndex < expression.length && parenCount > 0) {
        if (expression[endIndex] === "(") {
          parenCount++;
        } else if (expression[endIndex] === ")") {
          parenCount--;
        }
        endIndex++;
      }

      if (parenCount === 0) {
        const template = expression.substring(startIndex, endIndex - 1);
        const replacement = val
          .map((item, index) => {
            // Create a temporary response object with the current item and index
            const itemResponse = {
              ...workingResponse,
              [key]: item,
              [`${key}_index`]: index,
              [`${key}_item`]: item,
              // Add all the item's properties directly to the response for easier access
              ...item
            };
            // Process the template with the current item's data
            return findDynamicKeyInExpression(template, itemResponse, page);
          })
          .join(""); // Join without separator - let the template handle formatting

        // Replace the entire match including the template
        expression =
          expression.substring(0, loopMatch.index) +
          replacement +
          expression.substring(endIndex);

        // Reset regex to start from beginning since we modified the string
        loopRegex.lastIndex = 0;
      }
    }
  }

  // If value is array, expand it
  expression = expression.replace(
    /\$\(([^)\[\]]+)\)\.(\w+)/g,
    (_, key, property) => {
      const val = findAndSetKeyInObject(workingResponse, key);
      if (Array.isArray(val)) {
        return val.map((item) => item?.[property] ?? "");
      }
      if (val && typeof val === "object") {
        return val?.[property] ?? "";
      }
      return "";
    }
  );

  // Check if the expression is EXACTLY a single placeholder
  const isExactSinglePlaceholder = /^\$\(([^)]+)\)(\.\w+)?$/.test(expression);

  if (isExactSinglePlaceholder) {
    const match = expression.match(/\$\(([^)]+)\)(\.\w+)?/);
    const key = match[1];
    const property = match[2] ? match[2].slice(1) : ""; // Remove leading '.'

    const value = findAndSetKeyInObject(workingResponse, key);
    if (value === null || value === undefined) return "";
    return property &&
      typeof value === "object" &&
      isValueExist(value) &&
      property in value
      ? value[property] || ""
      : (value ?? "");
  }

  // If the expression contains multiple dynamic parts, replace them in the string
  return expression.replace(/\$\(([^)]+)\)(\.\w+)?/g, (_, key, property) => {
    const value = findAndSetKeyInObject(workingResponse, key);
    if (value === null || value === undefined) return "";

    return value &&
      property &&
      typeof value === "object" &&
      isValueExist(value) &&
      property.slice(1) in value
      ? value[property.slice(1)] || ""
      : (value ?? "");
  });
}

/**
 * Flattens a form template into one entry per field *instance* — i.e. per
 * {pageId, rowId, fieldId} location rather than per unique field id, so two
 * instances of the same field id living in different pages stay distinguishable.
 *
 * Used by `formBuilderStore.allFieldLocations` → the dashboard column picker,
 * which derives an unambiguous json_path from the returned `key` (see PHO-913)
 * and depends on this output staying byte-identical. The email builder's
 * variable palette no longer uses it — it reads responses instead
 * (`src/utils/emailTemplate/responseVariables.js`). The `list_items` recursion
 * and duplicate detection are the parts most likely to diverge if copied.
 *
 * @param {Object} template   form_templates row (uses `pages[]` — the page REFS)
 * @param {Object} pagesById  { [pageId]: form_pages row } — supplies `fields[]`
 * @param {Function} [resolveFieldType] maps a field to its catalog field_type.
 *   Defaults to reading `fieldObj`, which `Api.FormPages.getFormPageById(id, true)`
 *   attaches. The builder store passes its own lookup because it holds the
 *   catalog in memory and its page rows are not fetched with fields.
 * @returns {Array<{key,pageId,rowId,fieldId,label,fieldType,pageTag,isDuplicate}>}
 */
function flattenFormFields(
  template,
  pagesById,
  resolveFieldType = (field) => field?.fieldObj?.field_type ?? "?"
) {
  const out = [];
  const counts = new Map();

  const walkPages = (refs) => {
    for (const pageRef of refs ?? []) {
      const page = pagesById?.[pageRef.page_id];
      for (const grp of page?.fields ?? []) {
        for (const field of grp.group ?? []) {
          if (!field.id) continue;
          counts.set(field.id, (counts.get(field.id) ?? 0) + 1);
          out.push({
            key: `${pageRef.page_id}:${grp.row_id}:${field.id}`,
            pageId: pageRef.page_id,
            rowId: grp.row_id,
            fieldId: field.id,
            label: field.label || field.id,
            fieldType: resolveFieldType(field) ?? "?",
            pageTag: pageRef.tag || ""
          });
        }
      }
      if (Array.isArray(pageRef.list_items)) walkPages(pageRef.list_items);
    }
  };
  walkPages(template?.pages);

  // Mark entries whose field id shows up more than once so a picker can
  // prefix the page name on ambiguous options.
  for (const entry of out) {
    entry.isDuplicate = (counts.get(entry.fieldId) ?? 0) > 1;
  }
  return out;
}

/**
 * Loads a published form template together with every page it references.
 *
 * The page half of what `getResponseVariables` and the email delivery guard
 * need: the template row, and `{ [pageId]: form_pages row }` for every page in
 * its tree including nested `list_items`. Kept in one place so callers do not
 * keep separate copies of the recursion — which is the part that silently goes
 * stale when list pages change shape.
 *
 * Online-first and published-only, deliberately. `formBuilderStore` is not
 * used: it is a singleton that resets itself on load and prefers the draft
 * envelope, so reading through it would clobber form-builder state and offer
 * unpublished fields.
 *
 * @param {string} formTemplateId
 * @returns {Promise<{template: Object|null, pagesById: Object}>}
 */
async function loadFormPages(formTemplateId) {
  const templateRes = await useFormTemplateStore().getFormTemplateById(
    formTemplateId,
    true,
    false
  );
  if (templateRes?.error) throw templateRes.error;

  const template = templateRes?.data;
  if (!template) return { template: null, pagesById: {} };

  // Collect every referenced page id, including nested list_items pages.
  const pageIds = [];
  const collectPageIds = (refs) => {
    for (const pageRef of refs ?? []) {
      if (pageRef.page_id && !pageIds.includes(pageRef.page_id)) {
        pageIds.push(pageRef.page_id);
      }
      if (Array.isArray(pageRef.list_items)) collectPageIds(pageRef.list_items);
    }
  };
  collectPageIds(template.pages);

  // fetchWithFields=true attaches `fieldObj` (the form_fields catalog row) to
  // every field, which is where responseVariables reads field_type from.
  const formPageStore = useFormPageStore();
  const pagesById = {};
  const pageResults = await Promise.all(
    pageIds.map((pageId) => formPageStore.getFormPageById(pageId, true, false))
  );
  pageIds.forEach((pageId, idx) => {
    const page = pageResults[idx]?.data;
    if (page) pagesById[pageId] = page;
  });

  return { template, pagesById };
}

function generateFormTemplateResponse(
  templateObj,
  includeList = false,
  multiArrayResItems
) {
  try {
    const _formResObj = {};

    // Recursive function to handle dynamic nesting
    function handleNestedItems(parentObj, nestedItems, level = 0, parentKey) {
      if (includeList && nestedItems.length > 0 && parentKey) {
        parentObj[`${parentKey}_list_items`] = nestedItems.map(
          ({ pageObj, list_items, ...rest }) => ({ ...rest, list_items: [] })
        );
      }
      for (const item of nestedItems) {
        if (item.is_list && item.list_items && item.list_items.length > 0) {
          parentObj[item.id] = {};
          handleNestedItems(
            parentObj[item.id],
            item.list_items,
            level + 1,
            item.id
          );
        } else {
          if (
            item.pageObj &&
            item.pageObj.fields &&
            Array.isArray(item.pageObj.fields)
          ) {
            if (parentKey) {
              parentObj[item.id] = {};
            }
            for (const field of item.pageObj.fields) {
              let _tempObj = {};
              for (const actualField of field.group) {
                if (
                  !(
                    isValueExist(actualField.include_in_progress) ||
                    actualField.include_in_progress == false
                  )
                ) {
                  if (!field.is_multiple) {
                    if (parentKey) {
                      parentObj[item.id][actualField.id] =
                        actualField.value || null;
                    } else {
                      parentObj[actualField.id] = actualField.value || null;
                    }
                  }
                }
              }
              if (field.is_multiple) {
                let isValExistInArray = false;
                // update the tempobj value with value in the multiArrayResItems
                if (multiArrayResItems) {
                  let res = findAndSetKeyInObject(
                    multiArrayResItems,
                    field.row_id
                  );
                  if (res) {
                    _tempObj = res;
                    isValExistInArray = true;
                  }
                }

                if (parentKey) {
                  if (!Array.isArray(parentObj[item.id][field.row_id]))
                    parentObj[item.id][field.row_id] = [];
                  isValExistInArray
                    ? (parentObj[item.id][field.row_id] = _tempObj)
                    : parentObj[item.id][field.row_id].push(_tempObj);
                } else {
                  if (!Array.isArray(parentObj[field.row_id]))
                    parentObj[field.row_id] = [];
                  isValExistInArray
                    ? (parentObj[field.row_id] = _tempObj)
                    : parentObj[field.row_id].push(_tempObj);
                }
              }
            }
          }
        }
      }
    }

    // Ensure the parent object and its pages exist
    if (templateObj && templateObj.id && templateObj.pages) {
      _formResObj[templateObj.id] = {};
      for (const page of templateObj.pages) {
        if (page.is_list && page.list_items && page.list_items.length > 0) {
          _formResObj[templateObj.id][page.id] = {};
          handleNestedItems(
            _formResObj[templateObj.id][page.id],
            page.list_items,
            0,
            page.id
          );
        } else if (!page.is_list && page.pageObj?.fields) {
          _formResObj[templateObj.id][page.id] = {};
          handleNestedItems(
            _formResObj[templateObj.id][page.id],
            [page],
            0,
            null
          );
        } else if (
          page.template_type === Constants.enums.PAGE_TEMPLATE_TYPE.ATTENDEES
        ) {
          // Handle attendees pages - create a top-level key for the page
          _formResObj[templateObj.id][page.id] = page.value || [];
        }
      }
      // set global fields to the settings in the template table
      if (templateObj.settings?.global_fields) {
        // add global fields to the response
        templateObj.settings.global_fields.forEach((field) => {
          for (const page of templateObj.pages) {
            if (_formResObj[templateObj.id][page.id]) {
              _formResObj[templateObj.id][page.id][field.id] =
                field.value || null;
            }
          }
        });
      }
    } else {
      if (!(templateObj && templateObj.id) && templateObj.pages) {
        for (const page of templateObj.pages) {
          _formResObj[page.id] = {};
          if (page.is_list && page.list_items) {
            handleNestedItems(
              _formResObj[page.id],
              page.list_items,
              0,
              page.id
            );
          } else if (!page.is_list && page.pageObj?.fields) {
            handleNestedItems(_formResObj[page.id], [page], 0, null);
          } else if (
            page.template_type === Constants.enums.PAGE_TEMPLATE_TYPE.ATTENDEES
          ) {
            // Handle attendees pages - create a top-level key for the page
            _formResObj[page.id] = page.value || [];
          }
        }
      }
    }
    return _formResObj;
  } catch (error) {
    throw error;
  }
}

function populateListTemplateFromResponse(templateObj, responseObj) {
  try {
    function applyValues(parentObj, response) {
      // Collect items from ${uuid}_list_items (canonical) and plain list_items (legacy/old data)
      let responseItems = [];
      for (const key in response) {
        if (key.includes("_list_items")) {
          let _key = key.split("_list_items")[0];
          if (_key === parentObj.id && parentObj.is_list) {
            responseItems = response[key] || [];
          }
        }
      }
      // Also check plain list_items for old saved data
      if (Array.isArray(response.list_items) && parentObj.is_list) {
        const existingIds = new Set(responseItems.map((item) => item.id));
        const legacyItems = response.list_items.filter(
          (item) => !existingIds.has(item.id)
        );
        responseItems = [...responseItems, ...legacyItems];
      }

      if (responseItems.length > 0 && parentObj.is_list) {
        if (parentObj.list_items?.length) {
          // Merge template items with response items by index
          const merged = parentObj.list_items.map((item, i) => ({
            ...item,
            ...(responseItems[i] || {})
          }));
          // Append any user-added items that are beyond the template's length
          for (
            let i = parentObj.list_items.length;
            i < responseItems.length;
            i++
          ) {
            merged.push(responseItems[i]);
          }
          parentObj.list_items = merged;
        } else {
          parentObj.list_items = responseItems;
        }
      }

      // Then, apply values to nested children to avoid overwriting changes
      if (parentObj.is_list && Array.isArray(parentObj.list_items)) {
        for (let i = 0; i < parentObj.list_items.length; i++) {
          let item = parentObj.list_items[i];

          if (item.id && response[item.id]) {
            applyValues(item, response[item.id]); //  Process children after parents
            parentObj.list_items[i] = { ...item }; // Ensure reactivity
          }
        }
      }
    }

    if (templateObj && templateObj.id && templateObj.pages) {
      for (const page of templateObj.pages) {
        if (
          responseObj &&
          responseObj[templateObj.id] &&
          responseObj[templateObj.id][page.id]
        ) {
          if (
            page.template_type === Constants.enums.PAGE_TEMPLATE_TYPE.ATTENDEES
          ) {
            // Handle attendees pages - populate the page.value with response data
            page.value = responseObj[templateObj.id][page.id] || [];
          } else {
            applyValues(page, responseObj[templateObj.id][page.id]);
          }
        }
      }
    } else if (templateObj && templateObj.pages) {
      // Handle case where there's no template ID
      for (const page of templateObj.pages) {
        if (responseObj && responseObj[page.id]) {
          if (
            page.template_type === Constants.enums.PAGE_TEMPLATE_TYPE.ATTENDEES
          ) {
            // Handle attendees pages - populate the page.value with response data
            page.value = responseObj[page.id] || [];
          } else {
            applyValues(page, responseObj[page.id]);
          }
        }
      }
    }
    return templateObj;
  } catch (error) {
    console.error("ERROR IN HELPER FILE :", error);
    return null;
  }
}

function processFieldValues(fields, valObj = undefined) {
  if (!isValueExist(valObj)) {
    // Extract mode: Convert list of fields to an object
    const result = {};
    fields.forEach((fieldGroup) => {
      fieldGroup.group.forEach((field) => {
        if (field.save_to) {
          result[field.save_to] = null;
          if (isValueExist(field.value)) {
            if (field.value instanceof Date) {
              field.value = convertToLocalDate(field.value, "DD/MM/YYYY");
            }
            result[field.save_to] = field.value;
          }
        }
      });
    });
    return result;
  } else {
    // Insert mode: Populate fields with values from the object
    fields.forEach((fieldGroup) => {
      fieldGroup.group.forEach((field) => {
        if (field.save_to) {
          field["value"] = valObj[field.save_to] || null;
        } else if (field.include_in_progress != false) {
          field["value"] = findAndSetKeyInObject(valObj, field.id) || null;
        }
      });
    });
    return fields;
  }
}

const isUUID = (str) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

function calculateFilledPercentage(obj) {
  const result = {};

  function recurse(currentObj) {
    let totalKeys = 0;
    let filledKeys = 0;
    let childPercentages = [];

    for (const key in currentObj) {
      totalKeys++;

      if (
        currentObj[key] &&
        typeof currentObj[key] === "object" &&
        !Array.isArray(currentObj[key])
      ) {
        // Check if any key in the object is a UUID
        const hasUUIDKey = Object.keys(currentObj[key]).some((k) => isUUID(k));
        if (!hasUUIDKey) {
          filledKeys++;
        } else {
          // Recursively calculate percentage for child object
          const childPercentage = recurse(currentObj[key]);
          result[key] = childPercentage;
          childPercentages.push(childPercentage);
        }
      } else if (Array.isArray(currentObj[key])) {
        let hasAnyFilledItem = false;
        for (let i = 0; i < currentObj[key].length; i++) {
          const item = currentObj[key][i];
          if (item && typeof item === "object") {
            for (const fieldKey in item) {
              if (
                item[fieldKey] !== null &&
                item[fieldKey] !== undefined &&
                item[fieldKey] !== ""
              ) {
                hasAnyFilledItem = true;
                break;
              }
            }
            if (hasAnyFilledItem) break;
          }
        }

        if (hasAnyFilledItem) {
          filledKeys++;
          result[key] = 100;
        } else {
          result[key] = 0;
        }
      } else {
        // Count non-null and non-undefined values
        if (
          currentObj[key] !== null &&
          currentObj[key] !== undefined &&
          (typeof currentObj[key] === "boolean" ||
            currentObj[key].length > 0) &&
          !key.includes("_list_items")
        ) {
          filledKeys++;
        }
      }
    }

    // Calculate parent's percentage
    if (totalKeys > 0) {
      const directFillPercentage = (filledKeys / totalKeys) * 100;
      const childAverage =
        childPercentages.length > 0
          ? childPercentages.reduce((sum, p) => sum + p, 0) /
            childPercentages.length
          : 0;

      const finalPercentage = Number(
        Math.max(directFillPercentage, childAverage).toFixed(1)
      );

      return finalPercentage;
    }

    return 0;
  }

  for (const key in obj) {
    result[key] = recurse(obj[key]);
  }
  return result;
}

/**
 * Recursively searches for a key in an object and optionally gets or sets its value
 * @param {Object} obj - The object to search in
 * @param {string|Object} keyToFind - The key to find, can be a dot-notation path (e.g., "user.profile.role.value") or direct key reference
 * @param {*} [newValue] - Optional new value to set if found
 * @returns {*} The found value if not setting, or the updated object if setting
 * @example
 * // Finding a value with string path
 * const obj = { user: { profile: { role: { value: "admin" } } } };
 * findAndSetKeyInObject(obj, "user.profile.role.value"); // Returns "admin"
 *
 * // Finding a value with direct key
 * findAndSetKeyInObject(obj, "role"); // Returns { value: "admin" }
 *
 * // Setting a value
 * findAndSetKeyInObject(obj, "user.role.value", "user"); // Returns updated obj
 */
function findAndSetKeyInObject(
  obj,
  keyToFind,
  newValue,
  searchInPrivate = false
) {
  // Early return if obj is not valid
  if (!obj || typeof obj !== "object") {
    return newValue !== undefined ? obj : null;
  }

  let mainKey = keyToFind;
  let property = null;

  // Handle string paths — only first two segments are used (e.g. "a.b" → mainKey="a", property="b")
  if (typeof keyToFind === "string" && keyToFind.includes(".")) {
    const parts = keyToFind.split(".");
    mainKey = parts[0];
    property = parts[1];
  }

  const searched = new Set();

  function recursiveSearch(currentObj) {
    if (searched.has(currentObj)) {
      return;
    }
    searched.add(currentObj);

    // Direct property check first
    if (currentObj.hasOwnProperty(mainKey)) {
      if (newValue !== undefined) {
        currentObj[mainKey] = newValue;
      }
      const mainValue = currentObj[mainKey];
      const result =
        property && mainValue && typeof mainValue === "object"
          ? mainValue[property]
          : mainValue;
      if (newValue === undefined) {
        return result;
      }
    }

    const keys = Object.keys(currentObj);

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (!searchInPrivate && key.startsWith("_private_")) {
        continue;
      }
      const value = currentObj[key];
      if (value && typeof value === "object") {
        const found = recursiveSearch(value);
        if (found !== undefined) {
          return found;
        }
      }
    }
  }

  const result = recursiveSearch(obj);
  return newValue !== undefined ? obj : (result ?? null);
}

/**
 * Finds an object in the tree where obj.id === idToFind and sets obj[keyToSet] = value.
 * Used when the key is a list-item id (stored as "id") so we set the value on that object
 * instead of adding a new top-level key in the response.
 * @param {object} root - Root object to search (mutated in place)
 * @param {string} idToFind - id value to find (e.g. list item id)
 * @param {string} keyToSet - property to set on the found object
 * @param {*} value - value to set
 * @returns {boolean} true if an object was found and updated, false otherwise
 */
function findObjectByIdAndSetKey(root, idToFind, keyToSet, value) {
  if (!root || typeof root !== "object" || idToFind == null) return false;
  const searched = new Set();
  function walk(obj) {
    if (!obj || typeof obj !== "object" || searched.has(obj)) return false;
    searched.add(obj);
    if (obj.id === idToFind) {
      obj[keyToSet] = value;
      return true;
    }
    for (const k of Object.keys(obj)) {
      if (k.startsWith("_private_")) continue;
      const v = obj[k];
      if (v && typeof v === "object") {
        if (walk(v)) return true;
      }
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item && typeof item === "object" && walk(item)) return true;
        }
      }
    }
    return false;
  }
  return walk(root);
}

/**
 * Walks an explicit path (array of keys) into a nested object and returns the value at that location.
 * Unlike findAndSetKeyInObject this does NOT do a DFS — it follows the exact path provided,
 * so it is immune to wrong-match bugs when the same key exists in multiple branches.
 * @param {Object} obj - The object to navigate
 * @param {string[]} pathArray - Ordered array of keys, e.g. [templateId, pageId, itemId]
 * @returns {*} The value at the path, or null if any segment is missing
 */
function getValueByPath(obj, pathArray) {
  if (!obj || typeof obj !== "object" || !Array.isArray(pathArray)) return null;
  let current = obj;
  for (const key of pathArray) {
    if (current == null || typeof current !== "object") return null;
    current = current[key];
  }
  return current ?? null;
}

function setValueByPath(obj, path, value) {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  const parts = path.split(".");

  const root = deepCopy(obj ?? {});

  let cur = root;

  // follow the path and set the value
  for (let i = 0; i < parts.length; i++) {
    const key = parts[i];
    const isLast = i === parts.length - 1;

    if (isLast) {
      cur[key] = value;
    } else {
      // if the key is not exist, create a new object
      if (cur[key] == null || typeof cur[key] !== "object") {
        cur[key] = {};
      }
      cur = cur[key];
      console.log("CUR KEY :", key, cur);
    }
  }
  console.log("ROOT -----> :", deepCopy(root));
  return root;
}

// Negates numeric values in invoice fields based on config
function negateInvoiceFields(invoice, cfg) {
  const result = JSON.parse(JSON.stringify(invoice));
  for (const [_, invoiceObj] of Object.entries(result)) {
    for (const [_, fields] of Object.entries(invoiceObj)) {
      const { key, parent_path } = cfg;
      if (!isValueExist(parent_path)) {
        if (fields[key] != null && !isNaN(fields[key])) {
          fields[key] = (-1 * parseFloat(fields[key])).toFixed(2);
        }
        // If parent_path is provided, negate the value of the field at the specified path
      } else if (isValueExist(parent_path)) {
        const array = findAndSetKeyInObject(fields, parent_path);
        if (Array.isArray(array)) {
          array.forEach((item) => {
            if (item[key] != null && !isNaN(item[key])) {
              item[key] = -1 * item[key];
            }
          });
        }
      }
    }
  }

  return result;
}

function deepMerge(target, source) {
  // If target is not an object, initialize it as an empty object or take the source value directly
  if (target === null || typeof target !== "object") {
    return Array.isArray(source) ? [...source] : source;
  }

  if (source === null || typeof source !== "object") {
    return target;
  }

  // Handle object merging
  for (const key in source) {
    if (Array.isArray(source[key])) {
      // Always take source array value
      target[key] = [...source[key]];
    } else if (source[key] && typeof source[key] === "object") {
      // Ensure target[key] is an object before merging
      if (!target[key] || typeof target[key] !== "object") {
        target[key] = {};
      }
      target[key] = deepMerge(target[key], source[key]);
    } else {
      // Direct assignment for non-objects or if target is null
      target[key] = source[key];
    }
  }

  return target;
}

function copyWithNullValues(obj) {
  if (!obj || typeof obj !== "object") return null; // Handle non-object cases

  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      newObj[key] = null; // Set only top-level keys to null
    }
  }
  return newObj;
}

async function clearAllSiteData() {
  // Clear Local Storage & Session Storage
  localStorage.clear();
  sessionStorage.clear();

  // Clear IndexedDB
  if (window.indexedDB && indexedDB.databases) {
    const databases = await indexedDB.databases();
    databases.forEach((db) => indexedDB.deleteDatabase(db.name));
  }

  // Clear Cache Storage
  if ("caches" in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  }

  // Unregister Service Workers
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister())
    );
  }

  // Clear Cookies
  document.cookie.split(";").forEach((cookie) => {
    document.cookie = cookie
      .replace(/^ +/, "")
      .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`);
  });

  // Force reload to fully reset everything
  setTimeout(() => window.location.reload(), 100);

  console.log("All site data cleared.");
}

function rotateImage90Degrees(imageBlob) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      // Swap width/height for 90 degree rotation
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext("2d");

      // Rotate 90 degrees clockwise
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.translate(-canvas.height / 2, -canvas.width / 2);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        URL.revokeObjectURL(img.src);
        resolve(blob);
      }, imageBlob.type);
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(img.src);
      reject(error);
    };
    img.src = URL.createObjectURL(imageBlob);
  });
}

const resolveRecipient = async (recipient, data) => {
  try {
    switch (recipient) {
      case Constants.enums.EMAIL_RECIPIENT_TYPES.REQUESTOR:
      case Constants.enums.EMAIL_RECIPIENT_TYPES.SUBMITTER:
      case Constants.enums.EMAIL_RECIPIENT_TYPES.ME:
      case Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_EMAIL:
        return getCurrentUserEmail();
      case Constants.enums.EMAIL_RECIPIENT_TYPES.SUPERVISOR:
        return await getManagerEmail(getCurrentUserOid());
      case Constants.enums.EMAIL_RECIPIENT_TYPES.NO_REPLY_APP:
        return Constants.enums.NO_REPLY_EMAILS_ID.NO_REPLY_APP;
      default:
        return recipient;
    }
  } catch (error) {
    console.error("Error resolving recipient:", error);
    return null;
  }
};

const resolveRecipients = async (recipients, data = {}) => {
  if (Array.isArray(recipients)) {
    return Promise.all(recipients.map((r) => resolveRecipient(r, data)));
  }
  return resolveRecipient(recipients, data);
};

/**
 * The SUBMITTER / SUPERVISOR addresses send-email-v2 substitutes for those
 * checkboxes. The supervisor is looked up on every send; a failed lookup gives
 * null and the edge function skips it.
 */
const resolveRoleRecipients = async () => ({
  submitter: getCurrentUserEmail(),
  supervisor: await resolveRecipient(
    Constants.enums.EMAIL_RECIPIENT_TYPES.SUPERVISOR
  )
});

/**
 * @typedef {Object} Condition
 * @property {string} field - Field path to evaluate
 * @property {*} value - Expected value to compare against
 * @property {string} type - Comparison type (EQ, NOT_EQ, OR, IS, IN)
 */

/**
 * @typedef {Object} Variable
 * @property {Condition[]} conditions - Array of conditions to evaluate
 */

/**
 * @typedef {Object} TriggerConditions
 * @property {Object.<string, Variable>} variables - Object containing variable definitions
 * @property {string} [condition] - Optional condition combining variables
 */

/**
 * Evaluates complex conditional logic for form triggers based on form data
 * @param {TriggerConditions} triggerConditions - The trigger conditions configuration
 * @param {Object} formData - The form data to evaluate against
 * @returns {boolean} The result of evaluating all conditions
 * @example
 * // Simple condition
 * const triggerConditions = {
 *   variables: {
 *     "IS_ADMIN": {
 *       conditions: [{
 *         field: "user.role.value",
 *         value: "admin",
 *         type: "EQ"
 *       }]
 *     }
 *   },
 *   condition: "IS_ADMIN"
 * };
 *
 * // Complex condition with multiple variables
 * const triggerConditions2 = {
 *   variables: {
 *     "IS_ADMIN": {
 *       conditions: [{
 *         field: "user.role.value",
 *         value: "admin",
 *         type: "EQ"
 *       }]
 *     },
 *     "HAS_PERMISSION": {
 *       conditions: [{
 *         field: "user.permissions",
 *         value: ["read", "write"],
 *         type: "OR"
 *       }]
 *     }
 *   },
 *   condition: "IS_ADMIN AND HAS_PERMISSION"
 * };
 */
function evaluateTriggerConditions(triggerConditions, originalFormData) {
  let formData = originalFormData;
  try {
    if (!triggerConditions || !triggerConditions.variables) {
      return false;
    }

    const conditionVarName = triggerConditions.condition || "";
    const variableResults = {};

    // Evaluate each variable defined in triggerConditions
    for (const varName in triggerConditions.variables) {
      const variable = triggerConditions.variables[varName];
      const variableMet =
        variable.conditions &&
        variable.conditions.every((condition) => {
          const { field, type, replaceData, permissionsLevel } = condition;
          let { value } = condition;

          if (replaceData === "permissions") {
            const permissionsStore = usePermissionsStore();
            formData =
              permissionsStore.getCombinedPermissions(permissionsLevel);
          }

          if (value === Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_EMAIL) {
            value = getCurrentUserEmail();
          }

          if (
            field === Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_ROLE_CHECK
          ) {
            // find role id
            const roleId = Constants.enums.FLRA_USER_ROLES[value];

            // current user role
            const currentRoles = getCurrentUserRoles();

            // check if the role id is in the current roles
            const result = roleId ? currentRoles.includes(roleId) : false;
            return result;
          }

          if (type.toLowerCase() === "any_field_true" && Array.isArray(field)) {
            // Handle array of fields for ANY_FIELD_TRUE type
            // Check if any field in the array has a truthy value
            return field.some((fieldPath) => {
              const formValue = findAndSetKeyInObject(formData, fieldPath);
              return (
                formValue &&
                formValue !== "" &&
                formValue !== null &&
                formValue !== undefined
              );
            });
          }

          // Handle array of fields for ALL_FIELDS_TRUE type
          if (
            type.toLowerCase() === "all_fields_true" &&
            Array.isArray(field)
          ) {
            // Check if all fields in the array have truthy values
            return field.every((fieldPath) => {
              const formValue = findAndSetKeyInObject(formData, fieldPath);
              return (
                formValue &&
                formValue !== "" &&
                formValue !== null &&
                formValue !== undefined
              );
            });
          }

          // Handle array field validation - check if field contains an array with minimum length
          if (type.toLowerCase() === "array_min_length") {
            const formValue = findAndSetKeyInObject(formData, field);

            const minLength = value || 1; // Default to 1 if no value specified

            return Array.isArray(formValue) && formValue.length >= minLength;
          }

          // Handle array object validation - check if every object in array passes the same validation
          if (type.toLowerCase() === "array_every_valid") {
            const formValue = findAndSetKeyInObject(formData, field);

            if (!Array.isArray(formValue) || formValue.length === 0) {
              return false;
            }

            // Get the validation condition for each object
            const objectValidationCondition = value;
            if (!objectValidationCondition) {
              console.warn(
                "No validation condition provided for array_every_valid"
              );
              return false;
            }

            // Check if every object in the array passes the validation condition
            return formValue.every((arrayItem, index) => {
              // Create a temporary form data object with the current array item
              // This allows us to validate each object using the same condition
              const tempFormData = {
                ...formData,
                [field]: arrayItem
              };

              // Evaluate the validation condition for this specific array item
              const itemValidationResult = evaluateTriggerConditions(
                objectValidationCondition,
                tempFormData
              );

              return itemValidationResult;
            });
          }

          // Handle array object property validation - check if every object in array has valid properties
          if (type.toLowerCase() === "array_every_property_valid") {
            const formValue = findAndSetKeyInObject(formData, field);

            if (!Array.isArray(formValue) || formValue.length === 0) {
              return false;
            }

            // Get the validation configuration
            const validationConfig = value;
            if (
              !validationConfig ||
              !validationConfig.property ||
              !validationConfig.condition
            ) {
              console.warn(
                "Invalid validation config for array_every_property_valid. Expected: { property: 'fieldName', condition: {...} }"
              );
              return false;
            }

            const propertyPath = validationConfig.property;
            const propertyCondition = validationConfig.condition;

            // Check if every object in the array has the required property that passes validation
            return formValue.every((arrayItem, index) => {
              // Get the property value from the array item
              const propertyValue = findAndSetKeyInObject(
                arrayItem,
                propertyPath
              );

              // Create a temporary form data object with the property value
              const tempFormData = {
                ...formData,
                [propertyPath]: propertyValue
              };

              // Evaluate the validation condition for this specific property
              const propertyValidationResult = evaluateTriggerConditions(
                propertyCondition,
                tempFormData
              );

              return propertyValidationResult;
            });
          }

          // Use findAndSetKeyInObject instead of direct access

          const formValue = findAndSetKeyInObject(formData, field);

          // Apply the appropriate comparision type
          switch (type.toLowerCase()) {
            case Constants.enums.COMPARISON_TYPE.EQ:
            case Constants.enums.COMPARISON_TYPE.IS:
              return formValue === value;
            case Constants.enums.COMPARISON_TYPE.NOT_EQ:
              return formValue !== value;
            case Constants.enums.COMPARISON_TYPE.OR:
              return Array.isArray(value)
                ? value.includes(formValue)
                : formValue === value;
            case Constants.enums.COMPARISON_TYPE.GT: {
              const numericFormValue = parseFloat(formValue);
              const numericConditionValue = parseFloat(value);

              if (!isNaN(numericFormValue) && !isNaN(numericConditionValue)) {
                return numericFormValue > numericConditionValue;
              }
              return false;
            }
            case Constants.enums.COMPARISON_TYPE.LT: {
              const numericFormValue = parseFloat(formValue);
              const numericConditionValue = parseFloat(value);

              if (!isNaN(numericFormValue) && !isNaN(numericConditionValue)) {
                return numericFormValue < numericConditionValue;
              }
              return false;
            }
            case Constants.enums.COMPARISON_TYPE.IN:
              // Check if the given value exists within an array field
              return Array.isArray(formValue) && formValue.includes(value);
            default:
              console.warn("Unknown comparison type:", type);
              return false;
          }
        });

      variableResults[varName] = variableMet;
    }

    //  If condition directly references a variable, return its result
    if (conditionVarName && variableResults[conditionVarName] !== undefined) {
      const result = variableResults[conditionVarName];
      return result;
    }

    // Additional feature: Evaluating conditions between variables (e.g., VAR_1 NOT_EQ VAR_2)
    if (
      (Constants.enums.COMPARISON_TYPE.NOT_EQ,
      Constants.enums.COMPARISON_TYPE.EQ,
      Constants.enums.COMPARISON_TYPE.OR,
      Constants.enums.COMPARISON_TYPE.AND)
    ) {
      // Replace variable names with their evaluated boolean values
      const conditionExpression = conditionVarName.replace(/(\w+)/g, (match) =>
        variableResults[match] !== undefined ? variableResults[match] : match
      );

      // Convert condition keywords to JavaScript operators for evaluation
      const finalResult = eval(
        conditionExpression
          .replace(
            new RegExp(`\\b${Constants.enums.COMPARISON_TYPE.IS}\\b`, "g"),
            Constants.enums.COMPARISON_TYPE.JS_EQ
          )
          .replace(
            new RegExp(`\\b${Constants.enums.COMPARISON_TYPE.NOT_EQ}\\b`, "g"),
            Constants.enums.COMPARISON_TYPE.JS_NOT_EQ
          )
          .replace(
            new RegExp(`\\b${Constants.enums.COMPARISON_TYPE.AND}\\b`, "g"),
            Constants.enums.COMPARISON_TYPE.JS_AND
          )
          .replace(
            new RegExp(`\\b${Constants.enums.COMPARISON_TYPE.OR}\\b`, "g"),
            Constants.enums.COMPARISON_TYPE.JS_OR
          )
          .replace(
            new RegExp(`\\b${Constants.enums.COMPARISON_TYPE.NOT}\\b`, "g"),
            Constants.enums.COMPARISON_TYPE.JS_NOT
          )
      );
      return finalResult;
    }
    const result = Object.values(variableResults).some((v) => v === true);
    return result;
  } catch (error) {
    console.error("Error evaluating trigger conditions:", error);
    return false;
  }
}

const prepareEmailData = async (setting, formData, options = {}) => {
  try {
    const dataWithOid = {
      ...formData,
      oid: getCurrentUserOid()
    };

    const actionData = setting.action_data || {};

    const subject = findDynamicKeyInExpression(actionData.subject, formData);
    let emailData = {
      body: actionData.body,
      to: await resolveRecipients(setting.to.to, dataWithOid),
      cc: await resolveRecipients(setting.to.cc, dataWithOid),
      subject,
      from: Constants.enums.NO_REPLY_EMAILS_ID.NO_REPLY_APP,
      ...options
    };

    return emailData;
  } catch (error) {
    console.error("Error preparing email data:", error);
    throw new Error(`Failed to prepare email data: ${error.message}`);
  }
};

const convertGroupIdsToEmails = async (recipientList) => {
  if (!Array.isArray(recipientList) || recipientList.length === 0) {
    return [];
  }
  const profileStore = useProfileStore();

  const finalRecipients = await Promise.all(
    recipientList.map(async (recipient) => {
      // If the recipient is a number, treat it as a group ID and fetch emails
      if (typeof recipient === "number") {
        return await profileStore.fetchEmailsByDistributionGroup(recipient);
      }
      return recipient;
    })
  );

  return [...new Set(finalRecipients.flat())];
};

/**
 * Resolves email recipients from either email config or template defaults
 * Handles arrays, single values, and dynamic expressions
 */
const resolveEmailRecipients = async (
  emailConfigValue,
  defaultValue,
  templateData
) => {
  // Helper to process emails that might be comma-separated or in arrays
  const processEmails = (emails) => {
    if (!Array.isArray(emails)) {
      emails = [emails];
    }

    // Flatten and split comma-separated emails
    const processed = emails.flatMap((email) => {
      if (typeof email === "string") {
        // Split by comma and trim whitespace
        return email
          .split(",")
          .map((e) => e.trim())
          .filter((e) => e.length > 0);
      }
      return email;
    });

    // Remove duplicates and filter out empty values
    return [
      ...new Set(processed.filter((email) => email != null && email !== ""))
    ];
  };

  // Helper to process a single value (array or single) into normalized array
  const processValue = async (value) => {
    if (Array.isArray(value)) {
      // Map array values, handling dynamic expressions
      const processed = value.map((val) =>
        typeof val === "string" && val.startsWith("$")
          ? findDynamicKeyInExpression(val, templateData)
          : val
      );
      const result = await convertGroupIdsToEmails(
        await resolveRecipients(processed, templateData)
      );
      return processEmails(result);
    } else if (value != null) {
      // Single value - check if it's a dynamic expression (string starting with $)
      if (typeof value === "string" && value.startsWith("$")) {
        const resolved = findDynamicKeyInExpression(value, templateData);
        const result = await convertGroupIdsToEmails(
          await resolveRecipients([resolved], templateData)
        );
        return processEmails(result);
      } else {
        // Wrap single value in array (handles strings, numbers, etc.)
        const result = await convertGroupIdsToEmails(
          await resolveRecipients([value], templateData)
        );
        return processEmails(result);
      }
    }
    return [];
  };

  // Pages config takes priority, fallback to template defaults
  let result;
  if (isValueExist(emailConfigValue)) {
    result = await processValue(emailConfigValue);
  } else if (isValueExist(defaultValue)) {
    result = await processValue(defaultValue);
  } else {
    result = [];
  }

  return result;
};

/**
 * Send email with dev protection: in non-prod, redirects to current user and appends dev note.
 * Single place to manage so all callers get consistent behavior.
 * @param {Object} emailData - { to, cc?, subject, body, from?, attachments? }
 * @returns {Promise<Object>} EdgeFunction.sendEmail response
 */
async function sendEmail(emailData) {
  const toList = Array.isArray(emailData.to)
    ? emailData.to
    : emailData.to
      ? [emailData.to]
      : [];
  const ccList = Array.isArray(emailData.cc)
    ? emailData.cc
    : emailData.cc
      ? [emailData.cc]
      : [];

  let payload = { ...emailData, to: toList, cc: ccList };
  if (!isCurrentEnvironmentProd() && (toList.length || ccList.length)) {
    payload = {
      ...payload,
      body:
        (payload.body || "") +
        automatedMessagePlaceHolder([...toList, ...ccList]),
      to: [getCurrentUserEmail()],
      cc: []
    };
  }
  return EdgeFunction.sendEmail(payload);
}

/**
 * Build and send a single email using the same resolution logic as SEND_EMAIL_AUTO.
 * Resolves to/cc/from/subject/body from config (and optional email template), applies dev redirect, then sends.
 * @param {Object} templateData - Data for dynamic expressions (e.g. form data, responseId, currentURL)
 * @param {Object} emailConfig - { to, cc?, subject, body, from?, attachments?, email_template_id? }
 * @returns {Promise<void>}
 */
async function buildAndSendEmail(templateData, emailConfig) {
  if (isDraftEmailId(emailConfig.email_template_id)) {
    console.warn(
      "[buildAndSendEmail] skipped unpublished drafted email",
      emailConfig.email_template_id
    );
    return;
  }
  const emailTemplatesStore = useEmailTemplatesStore();
  const tplRes = isStringValueExist(emailConfig.email_template_id)
    ? await emailTemplatesStore.getEmailTemplateById(
        emailConfig.email_template_id
      )
    : null;
  const tpl = tplRes?.data || {};
  const { subject, body } = await renderTemplateMessage(
    tpl,
    templateData,
    emailConfig
  );

  let to = await resolveEmailRecipients(
    emailConfig.to,
    tpl.default_sending_data?.to,
    templateData
  );
  let cc = await resolveEmailRecipients(
    emailConfig.cc,
    tpl.default_sending_data?.cc,
    templateData
  );
  const from = isValueExist(emailConfig.from)
    ? emailConfig.from
    : (await resolveRecipient(tpl.default_sending_data?.from, templateData)) ||
      Constants.enums.NO_REPLY_EMAILS_ID.NO_REPLY_APP;

  const emailData = {
    from,
    to,
    subject: subject ?? "",
    body: body ?? "",
    attachments: emailConfig.attachments || [],
    cc: cc || []
  };
  const response = await sendEmail(emailData);
  if (response?.error) {
    throw new Error(response.error);
  }
}

// Processes a list of email trigger settings:
const processEmailTriggers = async (emailTriggers) => {
  if (!emailTriggers?.length) return;

  for (const setting of emailTriggers) {
    const { action_data = {}, trigger_conditions } = setting;
    action_data.to = action_data.to || [];
    action_data.cc = action_data.cc || [];

    setting.to = {
      to: [...action_data.to],
      cc: [...action_data.cc]
    };

    // Merge recipient lists if trigger conditions exist
    if (trigger_conditions) {
      if (trigger_conditions.to) {
        setting.to.to = [
          ...new Set([...setting.to.to, ...trigger_conditions.to])
        ];
      }
      if (trigger_conditions.cc) {
        setting.to.cc = [
          ...new Set([...setting.to.cc, ...trigger_conditions.cc])
        ];
      }
    }

    // Convert numeric group IDs into actual email addresses
    setting.to.to = await convertGroupIdsToEmails(setting.to.to);
    setting.to.cc = await convertGroupIdsToEmails(setting.to.cc);

    // Update action_data
    action_data.to = setting.to.to;
    action_data.cc = setting.to.cc;
    setting.action_data = action_data;
  }
};

function compareVersions(remoteVersion, localVersion) {
  try {
    const parts1 = remoteVersion.split(".").map(Number);
    const parts2 = localVersion.split(".").map(Number);

    console.log(`remoteVersion ${remoteVersion}, localVersion ${localVersion}`);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 < num2) {
        console.log("<----- remoteVersion is less than local version ----->");
        return -1;
      } else if (num1 > num2) {
        console.log(
          "<----- remoteVersion is greater than local version ----->"
        );
        return 1;
      }
    }

    return 0; // versions are equal
  } catch (error) {
    return 0; // versions are equal
  }
}

// Convert an enum key string to a readable format
const formatEnumLabel = (str) =>
  str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

// Convert an enum object into an array of options for dropdowns
const convertEnumToSelectOptions = (enumObj) => {
  return Object.entries(enumObj).map(([key, value]) => ({
    label: formatEnumLabel(key),
    value
  }));
};

// Get a readable label for an enum value
const getEnumValueLabel = (value, enumObj) => {
  const entry = Object.entries(enumObj).find(([_, val]) => val === value);
  return entry ? formatEnumLabel(entry[0]) : `Type ${value}`;
};

// Formats JSON data for display, truncating if too long
const formatJsonForDisplay = (data, maxLength = 50) => {
  if (!data) return "{}";
  const formatted = typeof data === "string" ? data : JSON.stringify(data);
  return formatted.length > maxLength
    ? `${formatted.substring(0, maxLength)}...`
    : formatted;
};

// Extracts unique values from an array of objects and formats them as select options
const extractUniqueValuesAsOptions = (array, field) => {
  if (!array) return [];
  const values = new Set(array.map((item) => item[field]));
  return Array.from(values).map((value) => ({
    label: formatEnumLabel(value),
    value: value
  }));
};

const savePdfToFilesystem = async (base64Pdf, filename) => {
  try {
    // IOS works with Directory.Data, Directory.Documents, Directory.External, Directory.ExternalStorage
    result = await Filesystem.writeFile({
      path: sanitizeFilename(filename),
      data: base64Pdf,
      directory: Directory.Documents
    });
  } catch (error) {
    console.error("Error saving PDF:", error);
  }
};

// Converts a base64 string to a downloadable File object with proper MIME type and extension
function base64ToFile(base64, filename = "document", mimeType = "") {
  const parts = base64.split(",");
  if (parts.length !== 2 || !parts[1]) {
    throw new Error("Invalid base64 format");
  }

  const binary = atob(parts[1]);
  const byteArray = new Uint8Array([...binary].map((c) => c.charCodeAt(0)));
  const type =
    mimeType ||
    parts[0].match(/data:(.*?);/)?.[1] ||
    "application/octet-stream";
  const extension = type.split("/")[1] || "bin";

  return new File([byteArray.buffer], `${filename}.${extension}`, {
    type
  });
}

const isValidEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const getCreditInvoiceMessage = () => {
  return "Are you sure you want to <b style='color: green'>Credit</b> this invoice? <br/><br/>This action will create a new invoice with the same details as the original invoice but with a credit amount.";
};

const getCreditAndRebillInvoiceMessage = () => {
  return "Are you sure you want to <b style='color: green'>Credit</b> and <b style='color: red'>Rebill</b> this invoice? <br/><br/>This action will create a new invoice with the same details as the original invoice but with a credit amount, and create another new invoice with the original amount which you can update with the new details.";
};
// Convert date filter preset to date range object
const getDateRangeFromPreset = (preset, useIsoFormat = false) => {
  if (!preset) return null;

  const today = moment().startOf("day");

  const formatDate = (date, endOfDay = false) =>
    useIsoFormat
      ? (endOfDay ? date.endOf("day") : date).toISOString()
      : (endOfDay ? date.endOf("day") : date).format("YYYY-MM-DD");

  switch (preset) {
    case Constants.enums.DATE_FILED.TODAY:
      return {
        from: formatDate(today),
        to: formatDate(today, true)
      };
    case Constants.enums.DATE_FILED.YESTERDAY: {
      const y = moment().subtract(1, "day").startOf("day");
      return {
        from: formatDate(y),
        to: formatDate(y, true)
      };
    }
    case Constants.enums.DATE_FILED.THIS_WEEK:
      return {
        from: formatDate(moment().startOf("week")),
        to: formatDate(moment().endOf("week"), true)
      };
    case Constants.enums.DATE_FILED.LAST_WEEK: {
      const start = moment().subtract(1, "week").startOf("week");
      const end = moment().subtract(1, "week").endOf("week");
      return {
        from: formatDate(start),
        to: formatDate(end, true)
      };
    }
    case Constants.enums.DATE_FILED.THIS_MONTH:
      return {
        from: formatDate(moment().startOf("month")),
        to: formatDate(moment().endOf("month"), true)
      };
    case Constants.enums.DATE_FILED.LAST_MONTH: {
      const start = moment().subtract(1, "month").startOf("month");
      const end = moment().subtract(1, "month").endOf("month");
      return {
        from: formatDate(start),
        to: formatDate(end, true)
      };
    }
    default:
      return null;
  }
};

// Generates a dynamic PDF filename using the form type and current timestamp
function generateFormFileName(formId, response = {}) {
  const formattedDate = moment().format("Do_MMM_YYYY_hh_mm_A").toLowerCase();

  const fieldId = Constants.enums.PDF_FILE_NAME_FILED[formId];
  const fieldValue = fieldId ? findAndSetKeyInObject(response, fieldId) : null;
  if (fieldValue) {
    // Use the specified fieldValue(e.g., invoiceNumber) if available
    return `${fieldValue}_${formattedDate}.pdf`;
  } else {
    const FORM_PREFIX_BY_ID = Object.entries(Constants.enums.FORM_TYPES).reduce(
      (acc, [key, val]) => {
        acc[val] = key.toLowerCase();
        return acc;
      },
      {}
    );
    const prefix = FORM_PREFIX_BY_ID[formId] || "form";
    return `${prefix}_${formattedDate}.pdf`;
  }
}

/**
 * Checks if the current form version matches the email versions
 * @param {string} responseId - The ID of the form template response
 * @param {Object} condition - The trigger condition object
 * @returns {Promise<boolean>} - Returns true if the condition is met, false otherwise
 */
async function checkEmailVersion(responseIdOrData, condition) {
  try {
    let conditionData;

    // Handle case where responseId is provided
    if (typeof responseIdOrData === "string") {
      // If the responseId is not provided, return true
      if (!isStringValueExist(responseIdOrData)) {
        return true;
      }

      // Import the store here to avoid circular dependencies
      const { useFormTemplateResponseStore } = await import(
        "@/stores/formTemplateResponseStore"
      );
      const formTemplateResponseStore = useFormTemplateResponseStore();

      // Fetch the form template response
      const response =
        await formTemplateResponseStore.getFormTemplateResponseById(
          responseIdOrData
        );

      if (response.error) throw response.error;

      // Extract email versions from the response
      const emailVersions = response.data.email_history.map(
        (item) => item.version
      );

      // Create condition data with current version and email versions
      conditionData = {
        version: deepCopy(response.data.version),
        emails: emailVersions,
        submission_status: findAndSetKeyInObject(
          response.data,
          Constants.enums.INVOICE_FORM_FIELD.SUBMISSION_STATUS
        )
      };
    } else {
      // Handle case where pre-processed data is provided
      conditionData = {
        ...responseIdOrData,
        // Map email history to versions if email_history is provided
        emails: responseIdOrData.emailHistory
          ? responseIdOrData.emailHistory.map((item) => item.version)
          : []
      };
    }

    // Copy the condition and update the value for VERSION_MATCHES_EMAILS
    const conditionCopy = deepCopy(condition);
    conditionCopy.variables.VERSION_MATCHES_EMAILS.conditions[0].value =
      conditionData.emails;

    // Evaluate the trigger conditions
    return evaluateTriggerConditions(conditionCopy, conditionData);
  } catch (error) {
    console.error("Error checking email version:", error);
    return false;
  }
}

/**
 * Generic function to evaluate and execute triggers based on timing and conditions
 * @param {string} timingType - The execution timing type of the trigger (e.g., "IMMEDIATELY", "CLICK_PREVIEW", etc.)
 * @param {Array} triggerList - List of trigger objects to evaluate
 * @param {Object} formData - The form data (usually response) used for evaluating trigger conditions
 * @param {Function} onTriggerActivated - Callback function to execute when a trigger condition is met (receives the matched trigger as argument)
 * @param {boolean} stopOnFirstMatch - Whether to stop evaluating after the first matched trigger (default: false)
 */
function evaluateTriggers(
  timingType,
  triggerList,
  formData,
  onTriggerActivated,
  stopOnFirstMatch = false
) {
  try {
    if (!Array.isArray(triggerList)) return;

    // Filter triggers that match the given timing type
    const filteredTriggers = triggerList.filter(
      (trigger) =>
        trigger[Constants.enums.TRIGGER_FIELD.EXECUTION_TIMING] === timingType
    );

    for (const trigger of filteredTriggers) {
      const isConditionMet = evaluateTriggerConditions(
        trigger.trigger_conditions,
        formData
      );

      if (isConditionMet) {
        // Execute the trigger action
        if (typeof onTriggerActivated === "function") {
          onTriggerActivated(trigger);
        }

        if (stopOnFirstMatch) break;
      }
    }
  } catch (error) {
    console.error("Error while evaluating triggers:", error);
  }
}

export const extractFromJsonPath = (obj, path) => {
  try {
    if (!path || path === "$") return obj;
    if (path === "response") return obj.response;

    let target = obj;
    if (path.startsWith("response->")) {
      path = path.replace(/^response->/, "");
      target = obj.response;
    }

    const [base, finalKey] = path.split("->>");
    const keys = base.split("->");

    for (let key of keys) {
      if (!target || typeof target !== "object") return "-";
      target = target[key];
    }

    return finalKey ? (target?.[finalKey] ?? "-") : (target ?? "-");
  } catch {
    return "-";
  }
};

function exportToCsv(filename, rows) {
  var processRow = function (row) {
    var finalVal = "";
    for (var j = 0; j < row.length; j++) {
      var innerValue = row[j] === null ? "" : row[j].toString();
      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleString();
      }
      var result = innerValue.replace(/"/g, '""');
      if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
      if (j > 0) finalVal += ",";
      finalVal += result;
    }
    return finalVal + "\n";
  };

  var csvFile = "";
  for (var i = 0; i < rows.length; i++) {
    csvFile += processRow(rows[i]);
  }

  var blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

async function resolveSignatureRefs(obj, options = {}) {
  if (!obj || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return Promise.all(obj.map((item) => resolveSignatureRefs(item, options)));
  }

  // This object IS a signature ref — inject signatureData
  if (obj.isSignatureRef === true && obj.id) {
    try {
      const signaturesStore = useSignaturesStore();
      const result = await signaturesStore.getSignatureById(obj.id, options);
      if (result?.data?.data) {
        return { ...obj, signatureData: result.data.data };
      }
      console.warn(
        "resolveSignatureRefs: no blob data for signature",
        obj.id,
        result
      );
    } catch (e) {
      console.warn("resolveSignatureRefs: failed to fetch", obj.id, e);
    }
    return obj;
  }

  // Recurse into all properties in parallel
  const entries = await Promise.all(
    Object.entries(obj).map(async ([key, val]) => [
      key,
      await resolveSignatureRefs(val, options)
    ])
  );
  return Object.fromEntries(entries);
}

// True if the signature object has either inline data or a resolvable ref.
// Use for validation/filter checks where you only need to know "is there a signature?".
function hasSignature(obj) {
  if (!obj || typeof obj !== "object") return false;
  if (typeof obj.signatureData === "string" && obj.signatureData.length > 0)
    return true;
  if (obj.isSignatureRef === true && obj.id) return true;
  return false;
}

// Returns the signature SVG data URI, resolving via the signatures store when needed.
// Use for non-template async resolution (e.g., building a payload before PDF generation).
async function resolveSignatureSrc(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (typeof obj.signatureData === "string" && obj.signatureData.length > 0) {
    return obj.signatureData;
  }
  if (obj.isSignatureRef === true && obj.id) {
    try {
      const signaturesStore = useSignaturesStore();
      const result = await signaturesStore.getSignatureById(obj.id);
      return result?.data?.data ?? null;
    } catch (e) {
      console.warn("resolveSignatureSrc: failed to fetch", obj.id, e);
      return null;
    }
  }
  return null;
}

async function generatePDF(actionType, params) {
  const { userStore, getFormId, response } = params;

  try {
    if (!userStore.isOnline) {
      showToast(
        "Error",
        `Can not download the PDF as the system is currently offline. \n\nPlease try again later.`,
        "error"
      );
      return;
    }

    let pdfLayout = await getPdfLayout(getFormId);

    if (
      actionType ===
      Constants.enums.NAVIGATION_BTN_ACTIONS.ON_CARWALD_INVOICE_PDF_PREVIEW
    ) {
      pdfLayout = await processCarwaldInvoicePDFPrep({
        pdfLayout,
        response,
        formId: getFormId
      });
    } else if (
      actionType === Constants.enums.NAVIGATION_BTN_ACTIONS.ON_FOE_PDF_PREVIEW
    ) {
      let _res = await processFoePDFPrep({
        pdfLayout,
        response
      });
      _res.pdfLayout = preprocessPdfLayoutTextValues(
        _res.pdfLayout,
        _res.response
      );
      const _foeResponseCopy = deepCopy(_res.response);
      if (!_foeResponseCopy)
        throw new Error("generatePDF: deepCopy of FOE response returned null");
      const _resolvedFoeResponse = await resolveSignatureRefs(_foeResponseCopy);
      let _base64Content = await pdfBase64(
        _res.pdfLayout,
        _resolvedFoeResponse
      );
      return _base64Content;
    }

    pdfLayout = preprocessPdfLayoutTextValues(pdfLayout, response);
    const _responseCopy = deepCopy(response);
    if (!_responseCopy)
      throw new Error("generatePDF: deepCopy of response returned null");
    const resolvedResponse = await resolveSignatureRefs(_responseCopy);
    const base64Content = await pdfBase64(pdfLayout, resolvedResponse);

    return base64Content;
  } catch (error) {
    throw error;
  }
}

async function getPdfLayout(formId) {
  try {
    const pdfLayoutStore = usePdfLayoutStore();
    let _res = await pdfLayoutStore.getPdfLayout({
      queryParams: [
        {
          field: "name",
          value: formId
        }
      ]
    });
    return _res.content;
  } catch (error) {
    throw error;
  }
}

async function processFoePDFPrep({ pdfLayout, response, formId }) {
  let _response = deepCopy(response);

  // list of items data key
  let _list_items = findAndSetKeyInObject(
    _response,
    "6319474f-2e01-4a00-864e-c0df9cbac695"
  );

  // this is list of obj, i want to first sort by asset_group and then do array of grouping as asset_group
  // so have obj as two keys group_name and data in the data have all the data nad name as group_name
  let _sorted_list_items = _list_items.sort((a, b) =>
    a.asset_group.localeCompare(b.asset_group)
  );
  let _grouped_list_items = _sorted_list_items.reduce((acc, item) => {
    let found = acc.find((g) => g.group_name === item.asset_group);
    if (!found) {
      acc.push({
        group_name: item.asset_group,
        data: [item]
      });
    } else {
      found.data.push(item);
    }
    return acc;
  }, []);
  // TODO: technical debt, we need to fix how the generate pdf function works currently, it should not work the way it's working now
  // to much happening and functions gets passed again to helper file even though it lives in helper file.
  response = findAndSetKeyInObject(
    _response,
    "6319474f-2e01-4a00-864e-c0df9cbac695",
    _grouped_list_items
  );

  return { pdfLayout, response };
}

async function processCarwaldInvoicePDFPrep({ pdfLayout, response, formId }) {
  // check if the private field data exists
  let _private_field_data = findAndSetKeyInObject(
    deepCopy(response),
    "_private_initial_invoice_obj"
  );

  // if the private field data does not exist then return the pdf layout
  if (!isValueExist(_private_field_data)) return pdfLayout;

  // generate the pdf base64 content
  let _base64Content = await pdfBase64(
    preprocessPdfLayoutTextValues(
      pdfLayout,
      deepCopy({
        [formId]: _private_field_data
      })
    ),
    deepCopy({
      [formId]: _private_field_data
    })
  );

  // get the additional content
  let _additionalContent = findAndSetKeyInObject(
    deepCopy(pdfLayout),
    "additionalContent"
  );

  // add the additional content to the pdf layout
  _additionalContent.push({
    type: "mandatory_mergePdf",
    value: [
      {
        value: _base64Content,
        name: `${getRandomUuid()}.pdf`,
        type: "application/pdf"
      }
    ]
  });

  return findAndSetKeyInObject(
    deepCopy(pdfLayout),
    "additionalContent",
    _additionalContent
  );
}

async function openSendEmailDialog(params) {
  const {
    formId,
    response,
    pdfPreviewObj,
    triggerSettingsStore,
    selectedEmailDoc,
    showSendEmailDialog,
    navigateHome = false,
    returnPromise = false,
    pdfSrc = null
  } = params;

  try {
    if (!pdfPreviewObj.value.fileName) {
      pdfPreviewObj.value.fileName = generateFormFileName(formId, response);
    }

    let emailData = {
      ...pdfPreviewObj.value.data,
      pdfSrc: pdfSrc || pdfPreviewObj.value.pdfSrc,
      fileName: pdfPreviewObj.value.fileName
    };

    // Fetch trigger settings for the current form
    const triggerList = await triggerSettingsStore.fetchTriggerSettings(formId);
    const emailTrigger = triggerList.find(
      (t) => t.op_type === Constants.enums.BEFORE_AFTER_ACTION_TYPES.SEND_EMAIL
    );

    if (emailTrigger && emailTrigger.action_data) {
      const actionData = emailTrigger.action_data;

      // loop through the TO's and see if theres any dynamic values needs to get from response
      const toValue = actionData.to?.value?.map((item) =>
        findDynamicKeyInExpression(item, deepCopy(response))
      );

      const ccValue = actionData.cc?.value?.map((item) =>
        findDynamicKeyInExpression(item, deepCopy(response))
      );

      // preparing the email object
      emailData = {
        ...emailData,
        from: {
          value: actionData.from?.value,
          editable: actionData.from?.editable
        },
        subject: {
          value: findDynamicKeyInExpression(
            actionData.subject?.value,
            deepCopy(response)
          ),
          editable: actionData.subject?.editable
        },
        defaultMessage: actionData.body,
        to: {
          value: toValue,
          editable: actionData.to?.editable
        },
        cc: {
          value: ccValue,
          editable: actionData.cc?.editable
        },
        showSupervisor: actionData.showSupervisor,
        showMyself: actionData.showMyself,
        after_event: emailTrigger?.action_data?.after_event ?? []
      };
    }

    // Set the selected email document data and open the dialog
    selectedEmailDoc.value = emailData;
    showSendEmailDialog.value = true;

    // Store the navigateHome flag for use in onSendEmailSuccess
    selectedEmailDoc.value.navigateHome = navigateHome;

    // If returnPromise is true, return a promise that resolves when the email is sent
    if (returnPromise) {
      return Promise.resolve();
    }
  } catch (error) {
    throw error;
  }
}

async function onSendEmailSuccess(params) {
  const {
    emailData,
    selectedEmailDoc,
    uploadStore,
    formTemplateResponseStore,
    fetchFormTemplateResponseById,
    formTemplateData,
    response,
    getResponseId,
    router,
    beforeAfterActionsHandler,
    showSendEmailDialog,
    pdfPreviewObj
  } = params;

  try {
    const pdfFile = base64ToFile(selectedEmailDoc.value.pdfSrc);
    // Upload the PDF file to Supabase storage
    const uploadResult = await uploadStore.uploadFormAttachment(pdfFile);

    // fetch the latest response from the DB to get the latest version
    const latestRes =
      await formTemplateResponseStore.getFormTemplateResponseById(
        getResponseId
      );

    // check latest version
    const latestVersion = latestRes?.data?.version || 1;

    // Construct an email history entry for logging
    const historyItem = {
      to: emailData.to,
      cc: emailData.cc,
      subject: emailData.subject || "",
      body: emailData.message || "",
      attachment_link: uploadResult.url,
      timestamp: new Date().toISOString(),
      from: emailData.from,
      sent_by: getCurrentUserEmail(),
      response: response.value,
      version: latestVersion
    };

    // Save the email history to the form response
    await formTemplateResponseStore.updateEmailHistory(
      historyItem,
      getResponseId
    );

    // If this was a promise-based email, resolve the promise
    if (selectedEmailDoc.value.emailPromise) {
      selectedEmailDoc.value.emailPromise.resolve();
    }

    // Check if we should navigate home based on the stored flag
    if (selectedEmailDoc.value.navigateHome) {
      // Navigate based on template settings if available
      if (formTemplateData.value.settings?.after_submit_navigate_to) {
        router.push(formTemplateData.value.settings.after_submit_navigate_to);
      } else {
        // Default navigation to home if no specific route is set
        router.push(Constants.enums.ROUTE_ENUM.HOME);
      }
    }

    if (selectedEmailDoc.value.after_event) {
      await beforeAfterActionsHandler(selectedEmailDoc.value.after_event);
    }
  } catch (error) {
    // If this was a promise-based email, reject the promise
    if (selectedEmailDoc.value.emailPromise) {
      selectedEmailDoc.value.emailPromise.reject(error);
    }
    throw error;
  } finally {
    showSendEmailDialog.value = false;
    pdfPreviewObj.value.isVisible = false;
  }
}

async function evaluateClickButtonTriggers(params) {
  const {
    buttonType,
    formId,
    triggerSettingsStore,
    response,
    confirmationDialogObj,
    beforeAfterActionsHandler
  } = params;
  try {
    const triggerList = await triggerSettingsStore.fetchTriggerSettings(formId);

    const enrichedFormData = {
      ...response,
      buttonType
    };

    evaluateTriggers(
      Constants.enums.TRIGGER_EXECUTION_TIMING.CLICK_BUTTON,
      triggerList,
      enrichedFormData,
      (trigger) => {
        if (
          trigger.op_type ===
          Constants.enums.BEFORE_AFTER_ACTION_TYPES.SHOW_CONFIRMATION_DIALOG
        ) {
          confirmationDialogObj.value = {
            isVisible: true,
            headerMsg: trigger.action_data?.header,
            message: trigger.action_data?.message,
            onConfirm: async () => {
              await beforeAfterActionsHandler(
                [trigger.action_data?.yes_action],
                triggerList
              );
            },
            onCancel: async () => {
              await beforeAfterActionsHandler(
                [trigger.action_data?.no_action],
                triggerList
              );
            }
          };
        } else if (
          trigger.op_type ===
          Constants.enums.BEFORE_AFTER_ACTION_TYPES.SHOW_ERROR_DIALOG
        ) {
          throw new Error(trigger.action_data?.message || "An error occurred");
        }
      },
      true // stopOnFirstMatch
    );
  } catch (error) {
    console.error("Error in evaluateClickButtonTriggers:", error);
    throw error;
  }
}

async function saveFormResponse(params) {
  const {
    createNew = false,
    customResponse = null,
    isEmbedded = false,
    response,
    formProgress,
    formId,
    responseId,
    formTemplateResponseStore,
    formTemplateResponse,
    shouldShowToast = true,
    emitFunction = null,
    requiredSync = false
  } = params;

  try {
    // Create data object for saving
    let dataObj = {
      response: customResponse || response,
      response_progress: formProgress || {},
      form_template_id: formId,
      id: responseId || getRandomUuid(),
      updated_at: new Date().toISOString(),
      version: createNew ? 1 : formTemplateResponse?.version || 1
    };

    // Delete ID for new responses
    if (!responseId || createNew) delete dataObj.id;

    let _res = await formTemplateResponseStore.setFormTemplateResponse(
      dataObj,
      requiredSync
    );
    if (_res.error) throw _res.error;

    if (createNew) return _res.id;

    const savedResponseId = _res.id || dataObj.id;

    // Handle event emission or toast notification
    if (isEmbedded && emitFunction) {
      emitFunction("response-saved", savedResponseId);
    } else if (shouldShowToast) {
      showToast("Successful", "Form Saved Successfully", "success");
    }

    return {
      id: savedResponseId,
      data: _res.data
    };
  } catch (error) {
    console.error("Error in saveFormResponse:", error);
    throw error;
  }
}

async function handleEmailVersionCheck(params) {
  const {
    responseId,
    fetchFormTemplateResponseById,
    formTemplateData,
    showConfirmationDialog,
    generatePDF,
    openSendEmailDialog,
    navigation = false,
    data,
    actionType
  } = params;

  // Skip if no response ID exists
  if (!isStringValueExist(responseId)) return;

  // Get latest template response data
  await fetchFormTemplateResponseById(formTemplateData);

  // Check if version matches emails
  const shouldShowDialog = !(await checkEmailVersion(
    responseId,
    data.condition
  ));

  if (shouldShowDialog) {
    // Show confirmation dialog
    const userResponse = await showConfirmationDialog({
      headerMsg: "Version Mismatch",
      message:
        "The last email was sent for an older version of this form. Would you like to send a new email?",
      showHazardIcon: false
    });

    if (userResponse) {
      // Generate PDF and open email dialog
      const pdfSrc = await generatePDF(actionType);
      return {
        confirmed: true,
        pdfSrc,
        emailPromise: await openSendEmailDialog(navigation, true, pdfSrc)
      };
    }
  }

  return { confirmed: false };
}

async function beforeAfterActionsHandler(params) {
  const {
    isDashboard,
    actions,
    triggerList = [],
    _preUpdatedResponse,
    response,
    formTemplateData,
    pageBuilderComponents,
    localTempStorage,
    getFormId,
    getResponseId,
    showConfirmationDialog,
    router,
    generatePDF,
    pdfPreviewObj,
    openSendEmailDialog,
    templateId,
    formTemplateResponseStore,
    saveFormResponse,
    triggerSettingsStore,
    btnParams,
    route,
    addMoreMenuObj
  } = params;

  for (const action of actions) {
    _preUpdatedResponse.value = deepCopy(_preUpdatedResponse.value || response);

    switch (action.type) {
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES
        .FETCH_AND_UPDATE_RESPONSE: {
        const queryPairs = getQueryPairs(route);
        let queryParams = [];
        if (action.data?.queryParams?.length) {
          queryParams = action.data.queryParams;
          for (let i = 0; i < queryParams.length; i++) {
            queryParams[i] = {
              ...queryParams[i],
              value: findDynamicKeyInExpression(
                queryParams[i].value,
                queryPairs
              )
            };
          }
        }

        const res = await formTemplateResponseStore.getTemplatesResponseList(
          {
            fetchAll: true,
            searchTxt: null,
            queryParams
          },
          action.data.tableName
        );
        if (res.error) {
          console.error("GET getTemplatesResponseList ERROR ::::", res.error);
          throw res.error;
        }
        const responseData =
          res.data && res.data.length > 0 ? res.data[0] : null;
        if (!responseData) {
          throw new Error(
            "No response data found for the fetch_and_update_response action"
          );
        }
        _preUpdatedResponse.value = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key,
          responseData
        );
        break;
      }
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.VALIDATE_FORM: {
        // Validate the current form/page
        if (!pageBuilderComponents) {
          throw new Error(
            "Page builder components not available for validation"
          );
        }

        let isValid = true;
        let firstInvalidField = null;

        // Validate all pages if no specific page is specified
        const pagesToValidate = action.data?.pageIds
          ? action.data.pageIds
          : Object.keys(pageBuilderComponents);

        for (const pageId of pagesToValidate) {
          const pageBuilder = pageBuilderComponents[pageId];
          if (pageBuilder && typeof pageBuilder.validatePage === "function") {
            const validation = await pageBuilder.validatePage();
            if (!validation.isValid) {
              isValid = false;
              if (!firstInvalidField && validation.invalidFields.length > 0) {
                firstInvalidField = validation.invalidFields[0];
              }
            }
          }
        }

        // If validation fails, show error and abort all other actions
        if (!isValid) {
          // Show error message if specified
          if (action.data?.errorMessage) {
            showToast("Validation Error", action.data.errorMessage, "error");
          } else {
            showToast(
              "Validation Error",
              "Please fill in all required fields correctly",
              "error"
            );
          }

          // Scroll to first invalid field if available
          if (firstInvalidField) {
            const pageBuilder =
              pageBuilderComponents[Object.keys(pageBuilderComponents)[0]];
            if (
              pageBuilder &&
              typeof pageBuilder.scrollToInvalidField === "function"
            ) {
              pageBuilder.scrollToInvalidField(firstInvalidField);
            }
          }

          // Throw error to abort all subsequent actions
          throw new Error("FORM_VALIDATION_FAILED");
        }

        break;
      }
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.GET_AND_SET_LATLONG: {
        const existingValue = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key
        );
        if (
          !isStringValueExist(existingValue) ||
          (isStringValueExist(existingValue) && action.data.force_update)
        ) {
          const location = await getCurrentLocationLatLong();
          _preUpdatedResponse.value = findAndSetKeyInObject(
            _preUpdatedResponse.value,
            action.data.key,
            location
          );
        }
        break;
      }
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.UPDATE_RESPONSE: {
        if (
          evaluateTriggerConditions(
            action.data.warn_config?.warn_condition,
            _preUpdatedResponse.value
          )
        ) {
          const userResponse = await showConfirmationDialog({
            headerMsg: action.data.warn_config?.warning_header_msg || "Warning",
            message:
              action.data.warn_config?.warning_msg ||
              "Placeholder Warning Message",
            showHazardIcon: false
          });

          if (!userResponse) {
            throw new Error("USER_CANCELLED");
          }
        }

        _preUpdatedResponse.value = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key,
          findDynamicKeyInExpression(action.data.value, deepCopy(response))
        );

        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.NEGATE_RESPONSE: {
        _preUpdatedResponse.value = negateInvoiceFields(
          deepCopy(_preUpdatedResponse.value),
          action.data
        );
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.ADD_PREFIX_TO_RESPONSE: {
        let currentValue = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key
        );
        const prefixedValue = `${action.data.pre_fix}${currentValue}`;
        _preUpdatedResponse.value = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key,
          prefixedValue
        );
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.VERSION_UPDATE_EMAIL: {
        const versionCheckParams = {
          responseId: getResponseId,
          fetchFormTemplateResponseById: params.fetchFormTemplateResponseById,
          formTemplateData: formTemplateData,
          showConfirmationDialog,
          generatePDF,
          openSendEmailDialog,
          navigation: action.nav !== undefined ? action.nav : true,
          data: action.data,
          actionType: action.data?.actionType || null
        };

        await handleEmailVersionCheck(versionCheckParams);
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.ROUTE_TO_PAGE: {
        // Check if the route_to value is "self" to route to the current form template
        if (action.data.route_to === "self") {
          const templateId = getFormId;
          const responseId = getResponseId;
          const routePath = `${Constants.enums.ROUTE_ENUM.ADMIN}${Constants.enums.ROUTE_ENUM.FORM_TEMPLATE}/${templateId}/${responseId}`;
          router.push(routePath);
        } else if (action.data.custom_route_to) {
          // TODO: TECHNICAL DEBT IT SHOULD BE FIXED
          const routeTo = action.data.route_to.replace(
            "$(getResponseId)",
            getResponseId
          );
          router.push(routeTo);
        } else {
          router.push(action.data.route_to);
        }
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.TOAST_NOTIFICATION: {
        showToast(action.data.title, action.data.message, action.data.severity);
        break;
      }

      // Set current timestamp
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.SET_CURRENT_TIMESTAMP: {
        const currentTimestamp = new Date().toISOString();

        _preUpdatedResponse.value = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key,
          currentTimestamp
        );

        if (action.data.formatted_key) {
          const format = action.data.format || "DD/MM/YY HH:mm:ss";
          const formattedValue = convertToLocalDate(currentTimestamp, format);

          _preUpdatedResponse.value = findAndSetKeyInObject(
            _preUpdatedResponse.value,
            action.data.formatted_key,
            formattedValue
          );
        }
        break;
      }
      // format timestamp
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.FORMAT_TIMESTAMP: {
        let _currentValue = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key
        );

        if (action.data.formatted_key) {
          const format = action.data.format || "DD/MM/YY HH:mm:ss";
          const formattedValue = convertToLocalDate(
            Date.parse(_currentValue),
            format
          );

          _preUpdatedResponse.value = findAndSetKeyInObject(
            _preUpdatedResponse.value,
            action.data.formatted_key,
            formattedValue
          );
        }
        break;
      }

      // Handle auto increment value generation
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.GENERATE_AUTO_INCREMENT:
      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.GET_AUTO_INCREMENT_VALUE: {
        const shouldReturnVal =
          action.type ===
          Constants.enums.BEFORE_AFTER_ACTION_TYPES.GET_AUTO_INCREMENT_VALUE;

        // When in Dashboard
        if (isDashboard) {
          const fieldId = action.data.id;

          try {
            const responseData = response?.[templateId];
            const targetFieldValue = responseData?.[fieldId];

            if (!responseData) {
              console.warn(`Response data for templateId not found`);
              break;
            }

            const allFields =
              formTemplateData?.pages?.flatMap((page) =>
                page.pageObj?.fields?.flatMap((group) => group.group)
              ) || [];

            // Find the target field definition
            const targetField = allFields.find((f) => f.id === fieldId);

            if (!targetField) {
              console.warn(
                `Field definition with id not found in formTemplateData`
              );
              break;
            }
            // Generate auto-increment value and update response
            const newValue = await generateAutoIncrementValue({
              field: targetField,
              response,
              templateId,
              relevantResponseData: deepCopy(targetFieldValue || null),
              updateStore: (val) => {
                response[templateId][fieldId] = val;
              },
              shouldReturnVal
            });

            if (newValue) {
              if (!_preUpdatedResponse.value) {
                _preUpdatedResponse.value = deepCopy(response);
              }

              _preUpdatedResponse.value = findAndSetKeyInObject(
                _preUpdatedResponse.value,
                fieldId,
                newValue
              );
            }
          } catch (err) {
            console.error("Auto Increment Error in Dashboard:", err);
          }
        }
        // When in Form page context
        else {
          // Find the page builder component containing the field
          for (const page of formTemplateData.pages) {
            if (!page.pageObj?.fields) continue;

            for (const fieldGroup of page.pageObj.fields) {
              for (const field of fieldGroup.group) {
                if (field.id !== action.data.id) continue;

                const pageBuilderComponent =
                  pageBuilderComponents.value?.[page.id] ||
                  pageBuilderComponents[page.id];

                if (!pageBuilderComponent?.generateAutoIncrementValue) continue;

                try {
                  const newValue =
                    await pageBuilderComponent.generateAutoIncrementValue(
                      action.data.id,
                      shouldReturnVal
                    );

                  // Always update the response with the new value
                  if (newValue) {
                    if (!_preUpdatedResponse.value) {
                      _preUpdatedResponse.value = deepCopy(response);
                    }

                    _preUpdatedResponse.value = findAndSetKeyInObject(
                      _preUpdatedResponse.value,
                      action.data.id,
                      newValue
                    );
                  }
                } catch (error) {
                  console.error(
                    "Error generating auto increment value:",
                    error
                  );
                }
                break;
              }
            }
          }
        }
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES
        .SET_CURRENT_FORM_RESPONSE_LINK: {
        let _url =
          isValueExist(action.data.include_base_url) &&
          action.data.include_base_url
            ? `${getBaseUrl()}${Constants.enums.ROUTE_ENUM.ADMIN}${Constants.enums.ROUTE_ENUM.FORM_TEMPLATE}/${getFormId}/${getResponseId}`
            : `${Constants.enums.ROUTE_ENUM.ADMIN}${Constants.enums.ROUTE_ENUM.FORM_TEMPLATE}/${getFormId}/${getResponseId}`;

        _preUpdatedResponse.value = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key,
          _url
        );
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES
        .SET_SUBMITTED_FORM_LINK_TO_LOCAL_TEMP_STORAGE_OBJ: {
        let _newId = findAndSetKeyInObject(
          localTempStorage.value,
          "newSubmissionId"
        );

        localTempStorage.value[action.data.key] =
          isValueExist(action.data.include_base_url) &&
          action.data.include_base_url
            ? `${getBaseUrl()}${Constants.enums.ROUTE_ENUM.ADMIN}${Constants.enums.ROUTE_ENUM.FORM_TEMPLATE}/${getFormId}/${_newId}`
            : `${Constants.enums.ROUTE_ENUM.ADMIN}${Constants.enums.ROUTE_ENUM.FORM_TEMPLATE}/${getFormId}/${_newId}`;
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES
        .GET_SUBMITTED_FORM_LINK_TO_LOCAL_TEMP_STORAGE_OBJ: {
        action.data.value = findDynamicKeyInExpression(
          action.data.value,
          localTempStorage.value
        );

        _preUpdatedResponse.value = findAndSetKeyInObject(
          _preUpdatedResponse.value,
          action.data.key,
          action.data.value
        );
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.SEND_EMAIL: {
        const refId = parseInt(action.ref_trigger_id);
        const emailTrigger = refId
          ? triggerList.find(
              (t) => t.id === action.ref_trigger_id || t.id === refId
            )
          : null;

        const actionType = emailTrigger?.action_data?.actionType || null;
        pdfPreviewObj.value.pdfSrc = await generatePDF(actionType);
        await openSendEmailDialog(emailTrigger);
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.SEND_EMAIL_AUTO: {
        // Handle multiple emails with trigger conditions
        if (Array.isArray(action.data.emails)) {
          for (const emailConfig of action.data.emails) {
            try {
              // A drafted email (form builder drawer) has no email_templates
              // row until the form is published — e.g. in ?draft=1 preview.
              if (isDraftEmailId(emailConfig.email_template_id)) {
                console.warn(
                  "[SEND_EMAIL_AUTO] skipped unpublished drafted email",
                  emailConfig.email_template_id
                );
                continue;
              }
              // Evaluate trigger conditions for this email
              const shouldSendEmail = isValueExist(
                emailConfig.triggerConditions
              )
                ? evaluateTriggerConditions(
                    emailConfig.triggerConditions,
                    _preUpdatedResponse.value
                  )
                : true;

              if (shouldSendEmail) {
                // Create template data with response ID included
                const templateData = {
                  ..._preUpdatedResponse.value,
                  responseId: getResponseId,
                  currentURL: getAppURL()
                };

                // Fetch subject/body from email_templates via store
                const emailTemplatesStore = useEmailTemplatesStore();
                // return
                const tplRes = isStringValueExist(emailConfig.email_template_id)
                  ? await emailTemplatesStore.getEmailTemplateById(
                      emailConfig.email_template_id
                    )
                  : null;
                const tpl = tplRes?.data || {};
                const { subject, body } = await renderTemplateMessage(
                  tpl,
                  templateData,
                  emailConfig
                );
                // send-email-v2 decides the recipients: the template's
                // default_sending_data plus every matching send config.
                await EdgeFunction.sendEmailV2({
                  emailTemplateId: emailConfig.email_template_id,
                  data: templateData,
                  roleRecipients: await resolveRoleRecipients(),
                  subject,
                  body,
                  attachments: emailConfig.attachments || []
                });
                showToast("Success", "Email sent successfully", "success");
              } else {
                showToast(
                  "Error",
                  "Email not sent - trigger conditions not met for: " +
                    emailConfig.to,
                  "error"
                );
              }
            } catch (error) {
              showToast("Error", "Error sending email: " + error, "error");
              console.error("Error sending email:", error);
            }
          }
        }
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.TRIGGER_CLICK_BUTTON: {
        await evaluateTriggers(
          action.data.execution_timing,
          triggerList.length
            ? triggerList
            : await triggerSettingsStore.fetchTriggerSettings(getFormId),
          response,
          (trigger) => {
            if (
              trigger.op_type ===
              Constants.enums.BEFORE_AFTER_ACTION_TYPES.SHOW_ERROR_DIALOG
            ) {
              throw new Error(
                trigger.action_data.message || "Error condition met"
              );
            }
          }
        );
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.OPEN_ADD_MORE_MENU: {
        let showDialog = true;

        if (action.data.show_dialog) {
          showDialog = evaluateTriggerConditions(
            action.data.show_dialog,
            _preUpdatedResponse.value
          );
        }
        if (showDialog) {
          await handleAddMoreMenu({
            config: action.data,
            ...params,
            response: _preUpdatedResponse
          });
        }
        break;
      }

      case Constants.enums.BEFORE_AFTER_ACTION_TYPES.RUN_ACTION_FLOW: {
        const onClick = action.data.onClick;
        const beforeActions = action.data.before_event;
        const afterActions = action.data.after_event;

        await onNavigationBtnClick({
          ...btnParams,
          type: onClick,
          beforeActions,
          afterActions
        });
        break;
      }

      default:
        break;
    }

    // Update remote data if needed
    if (
      !isValueExist(action.update_remote_data) ||
      action.update_remote_data != false
    ) {
      // if key not set or value if undefined or null then update local data in store to get UI updated
      formTemplateResponseStore.setResponse(
        templateId,
        _preUpdatedResponse.value[templateId]
      );
      // if set to true in DB then update in remote DB
      if (action.update_remote_data == true) {
        await saveFormResponse(false, _preUpdatedResponse.value);
      }
    }
  }
}

// Handles navigation button clicks and executes before/after actions
async function onNavigationBtnClick(params) {
  const {
    isDashboard,
    type,
    beforeActions,
    afterActions,
    config,
    _preUpdatedResponse,
    saveFormResponse,
    generatePDF,
    pdfPreviewObj,
    router,
    evaluateClickButtonTriggers,
    triggerSettingsStore,
    activeStep,
    response,
    formTemplateData,
    getFormId,
    localTempStorage,
    confirmationDialogObj,
    isEmbedded,
    onEmbeddedAction,
    beforeAfterActionsHandler
  } = params;

  try {
    // reset the pre updated response
    if (_preUpdatedResponse) {
      _preUpdatedResponse.value = null;
    }
    // execute the before actions
    if (beforeActions) {
      try {
        await beforeAfterActionsHandler(beforeActions);
      } catch (error) {
        // If the error is our special cancellation error, just return
        if (error.message === "USER_CANCELLED") {
          return;
        }
        // If the error is our form validation error, just return
        if (error.message === "FORM_VALIDATION_FAILED") {
          return;
        }
        // Otherwise, rethrow the error to be caught by the outer try/catch
        throw error;
      }
    }

    if (type) {
      switch (type) {
        // moving to next page
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_NEXT:
          if (isEmbedded && onEmbeddedAction) {
            await onEmbeddedAction("next");
          } else {
            activeStep.value++;
          }
          break;
        // going back to previous page
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_BACK:
          if (isEmbedded && onEmbeddedAction) {
            await onEmbeddedAction("back");
          } else {
            activeStep.value--;
          }
          break;

        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_PDF_PREVIEW: {
          await saveFormResponse();
          pdfPreviewObj.value.pdfSrc = await generatePDF();
          const previewData = {
            flraNum: "Preview",
            form_template_id: getFormId
          };

          pdfPreviewObj.value = {
            ...pdfPreviewObj.value,
            isVisible: true,
            data: previewData
          };
          break;
        }
        case Constants.enums.NAVIGATION_BTN_ACTIONS
          .ON_CARWALD_INVOICE_PDF_PREVIEW: {
          if (!isDashboard) {
            await saveFormResponse();
          }
          pdfPreviewObj.value.pdfSrc = await generatePDF(
            Constants.enums.NAVIGATION_BTN_ACTIONS
              .ON_CARWALD_INVOICE_PDF_PREVIEW
          );
          const previewData = {
            flraNum: "Preview",
            form_template_id: getFormId
          };

          if (
            getFormId === Constants.enums.STATIC_FORM_TEMPLATE_ID?.INVOICE_FORMS
          ) {
            previewData.submission_status = findAndSetKeyInObject(
              response.value,
              Constants.enums.INVOICE_FORM_FIELD.SUBMISSION_STATUS
            );
          }

          pdfPreviewObj.value = {
            ...pdfPreviewObj.value,
            isVisible: true,
            data: previewData
          };
          break;
        }
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_FOE_PDF_PREVIEW: {
          if (!isDashboard) {
            await saveFormResponse();
          }
          pdfPreviewObj.value.pdfSrc = await generatePDF(
            Constants.enums.NAVIGATION_BTN_ACTIONS.ON_FOE_PDF_PREVIEW
          );

          pdfPreviewObj.value = {
            ...pdfPreviewObj.value,
            isVisible: true,
            data: null
          };
          break;
        }
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_DYNAMIC_REPORT: {
          if (isEmbedded && onEmbeddedAction) {
            await onEmbeddedAction(params);
          } else {
            throw new Error(
              "ON_DYNAMIC_REPORT action is only supported in embedded mode"
            );
          }
          break;
        }
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_SUBMIT:
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_SAVE:
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_UPDATE:
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_COMPLETE:
          await saveFormResponse();
          // check if setting has any value else go with default navigation

          if (isEmbedded && onEmbeddedAction) {
            // When embedded, notify the parent component about the action
            await onEmbeddedAction(
              type === Constants.enums.NAVIGATION_BTN_ACTIONS.ON_SAVE
                ? "save"
                : "complete"
            );
          } else {
            // Standalone behavior
            if (
              formTemplateData.value.settings &&
              formTemplateData.value.settings.after_submit_navigate_to
            ) {
              router.replace(
                formTemplateData.value.settings.after_submit_navigate_to
              );
              return;
            }
          }
          // default route
          if (
            formTemplateData.value.settings?.do_default_navigation === false
          ) {
            break;
          }
          router.replace(
            Constants.enums.ROUTE_ENUM.ADMIN +
              Constants.enums.ROUTE_ENUM.FORM_TEMPLATES_RESPONSES
          );
          break;
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_CARWALD_INVOICE_CREDIT:
          confirmationDialogObj.value = {
            ...confirmationDialogObj.value,
            isVisible: true,
            headerMsg: config?.warning_header_msg || "Warning",
            message: config?.warning_msg || getCreditInvoiceMessage(),
            onConfirm: async () => {
              let _newId = await saveFormResponse(
                true,
                _preUpdatedResponse.value
              );
              // Save the new data to local storage before executing after actions
              localTempStorage.value["newSubmissionId"] = _newId;
              // resetting the value to null to not get the changes that we'd have done on beforeactions
              _preUpdatedResponse.value = null;
              await beforeAfterActionsHandler(afterActions, null, params);
              if (params.onActionComplete) {
                params.onActionComplete();
              }
            }
          };
          // return to avoid executing the after actions
          return;
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_CARWALD_INVOICE_REBILL:
          let _newId = await saveFormResponse(true, _preUpdatedResponse.value);
          // Save the new data to local storage before executing after actions
          localTempStorage.value["newSubmissionId"] = _newId;
          // resetting the value to null to not get the changes that we'd have done on beforeactions
          _preUpdatedResponse.value = null;
          await beforeAfterActionsHandler(afterActions, null, params);
          return;
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_SEND_EMAIL: {
          if (params.generatePDF && params.openSendEmailDialog) {
            const pdfSrc = await params.generatePDF();
            await params.openSendEmailDialog(true, false, pdfSrc);
          }
          break;
        }
        case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_CANCEL:
          if (config?.warning_msg || config?.warning_header_msg) {
            confirmationDialogObj.value = {
              isVisible: true,
              headerMsg: config?.warning_header_msg || "Warning",
              message: config?.warning_msg || "Are you sure?",
              onConfirm: async () => {
                await saveFormResponse(false, _preUpdatedResponse.value);
                _preUpdatedResponse.value = null;
                await beforeAfterActionsHandler(afterActions, null, params);

                if (params.onActionComplete) {
                  params.onActionComplete();
                }
              }
            };
            return;
          }
          break;
        case Constants.enums.NAVIGATION_BTN_ACTIONS.OPEN_ADD_MORE_MENU:
          await handleAddMoreMenu(params);
          break;
        default:
          break;
      }
      await evaluateClickButtonTriggers(type);
    }
    const triggerList =
      await triggerSettingsStore.fetchTriggerSettings(getFormId);

    if (afterActions) {
      // resetting the value to null to not get the changes that we'd have done on beforeactions
      if (_preUpdatedResponse) {
        _preUpdatedResponse.value = null;
      }
      await beforeAfterActionsHandler(afterActions, triggerList);
    }
  } catch (error) {
    console.log("error from onNavigationBtnClick :", error);
    throw error;
  }
}

const generateAutoIncrementValue = async ({
  field,
  response,
  templateId,
  relevantResponseData,
  updateStore,
  shouldReturnVal = false
}) => {
  try {
    if (
      shouldReturnVal ||
      (field.fieldObj.field_type ===
        Constants.enums.FIELD_TYPES.AUTO_INCREMENT &&
        !isValueExist(field.value))
    ) {
      // get the last value from the DB table
      const sequenceStore = useSequenceStore();
      let value = await sequenceStore.getNextSequenceValue(
        await sequenceStore.getSequenceName(
          field.seq_group,
          response[templateId]
        )
      );

      if (value) {
        value = parseInt(value);
        // add prefix and suffix if any
        if (field.prefix) {
          value = field.prefix + value;
        }
        if (field.suffix) {
          value = value + field.suffix;
        }

        // if the function is called with shouldReturnVal then return the value, no need to update the store
        if (shouldReturnVal) {
          return value;
        }

        field.value = value;
        // UPDATE THE STORE VALUE
        if (relevantResponseData !== value) {
          updateStore(value);
        }
        return value;
      }
    }
    return null;
  } catch (error) {
    console.error("Error generating auto increment value:", error);
    throw error;
  }
};

/**
 * Helper function to process auto-increment for add more menu scenarios
 *
 * This function handles auto-increment for the main form template when the add more menu is saved.
 * Auto-increments will only ever be in the main form, not in the add more menu template.
 * It can be configured through the config.auto_increment_fields parameter in the navigation button configuration.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.formTemplateData - The main form template data
 * @param {Object} params.response - The current form response
 * @param {string} params.templateId - The main form template ID
 * @param {Array} params.autoIncrementFields - Array of field IDs to auto-increment
 *
 * @example
 * // Configuration example in navigation button:
 * {
 *   type: "OPEN_ADD_MORE_MENU",
 *   config: {
 *     template_id: "add-more-template-id",
 *     title: "Add New Item",
 *     navigate_to: "/some-path",
 *     auto_increment_fields: [
 *       "bde8f2ff-ff91-407b-87e8-db980a0850ef"
 *     ]
 *   }
 * }
 */
const processAddMoreMenuAutoIncrement = async ({
  formTemplateData,
  response,
  templateId,
  autoIncrementFields
}) => {
  try {
    if (
      !autoIncrementFields ||
      !Array.isArray(autoIncrementFields) ||
      autoIncrementFields.length === 0
    ) {
      return;
    }

    const allFields =
      formTemplateData.value?.pages?.flatMap((page) =>
        page.pageObj?.fields?.flatMap((group) => group.group)
      ) || [];

    // Process auto-increment for main form template only
    for (const fieldId of autoIncrementFields) {
      const targetField = allFields.find((f) => f.id === fieldId);

      if (targetField) {
        const currentValue = response[templateId]?.[fieldId];

        const newValue = await generateAutoIncrementValue({
          field: targetField,
          response,
          templateId,
          relevantResponseData: deepCopy(currentValue || null),
          updateStore: () => {}, // Not used when shouldReturnVal is true
          shouldReturnVal: true
        });

        if (newValue) {
          // Update the response directly in this function
          if (!response[templateId]) {
            response[templateId] = {};
          }
          findAndSetKeyInObject(response, fieldId, newValue);
        }
      }
    }
  } catch (error) {
    console.error("Error processing add more menu auto-increment:", error);
    // Don't throw the error to prevent breaking the add more menu save flow
    // Just log it for debugging purposes
  }
};

function getDefaultDateValue(mode = "today") {
  const offsetMap = {
    tomorrow: 1,
    next_week: 7,
    today: 0
  };

  const offset = offsetMap[mode] ?? 0;
  const date = new Date();
  date.setDate(date.getDate() + offset);

  return date;
}
// function for better console logging
function consoleSuccess(message) {
  console.log(`%c✔️ ${message}`, "color: green; background-color: #ccffcc;");
}

// here implement a function that will pull the lat long of current location
async function getCurrentLocationLatLong() {
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 2000 // 2 second timeout
      });
    });
    console.log("POSITION >>>>>>>>> :", position.coords);
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    };
  } catch (error) {
    console.error("Error getting current location:", error);
    return {
      latitude: 0.0,
      longitude: 0.0
    };
  }
}

const handleDashboardAction = (actionName, params) => {
  const { router, staticFormTemplateId, emit, route, onClickRoute } = params;

  switch (actionName) {
    case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_NEW_FORM:
      const queryPairs = getQueryPairs(route);
      if (onClickRoute) {
        const dynamicRoute = findDynamicKeyInExpression(
          onClickRoute,
          queryPairs
        );
        handleRouteNavigation(router, route, dynamicRoute);
      } else if (staticFormTemplateId) {
        handleRouteNavigation(
          router,
          route,
          Constants.enums.ROUTE_ENUM.ADMIN +
            Constants.enums.ROUTE_ENUM.FORM_TEMPLATE +
            `/${staticFormTemplateId}`
        );
      }
      break;

    default:
      emit("header-button-click", actionName);
      break;
  }
};

// Query params that are control flags, not filter values — exclude from getQueryPairs.
// `hiddenFilters` is a comma-separated list of filter fields that should be applied
// but NOT shown as pills in the UI (see getHiddenFilterFields).
const RESERVED_QUERY_PARAMS = new Set(["hiddenFilters"]);

const getQueryPairs = (route) => {
  const pairs = {};
  let queryParams;

  if (route) {
    // Use route.query if route is provided
    queryParams = route.query;
  } else {
    // Fallback to window.location if no route provided
    const currentRoute = window.location;
    const searchParams = new URLSearchParams(currentRoute.search);
    queryParams = Object.fromEntries(searchParams.entries());
  }

  // Handle key-value pairs
  Object.entries(queryParams)
    .filter(([key]) => key.startsWith("key"))
    .forEach(([key, value]) => {
      const valueKey = `value${key.substring(3)}`;
      if (queryParams[valueKey]) {
        pairs[value] = queryParams[valueKey];
      }
    });

  // Handle direct query params that aren't key-value pairs or reserved control flags
  Object.entries(queryParams)
    .filter(
      ([key]) =>
        !key.startsWith("key") &&
        !key.startsWith("value") &&
        !RESERVED_QUERY_PARAMS.has(key)
    )
    .forEach(([key, value]) => {
      pairs[key] = value;
    });

  // Replace any USER_ID values with actual user ID
  Object.entries(pairs).forEach(([key, value]) => {
    if (value === Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_ID) {
      pairs[key] = getCurrentUserId();
    }
  });

  return pairs;
};

// Returns the set of filter field names that should be hidden from the UI
// based on the `hiddenFilters` query param (comma-separated list).
// e.g. `?created_by=USER_ID&hiddenFilters=created_by` → applies the created_by
// filter but does not render a pill for it.
const getHiddenFilterFields = (route) => {
  let raw;
  if (route) {
    raw = route.query?.hiddenFilters;
  } else {
    const searchParams = new URLSearchParams(window.location.search);
    raw = searchParams.get("hiddenFilters");
  }
  if (!raw) return new Set();
  const list = Array.isArray(raw) ? raw : String(raw).split(",");
  return new Set(list.map((s) => s.trim()).filter(Boolean));
};

function isFieldHidden(field, formData) {
  if (field.hidden === true) return true;
  if (field.hidden_condition) {
    return evaluateTriggerConditions(
      field.hidden_condition,
      deepCopy(formData)
    );
  }
  return false;
}

// Helper function to handle route navigation with reload if needed
const handleRouteNavigation = async (router, route, targetPath) => {
  // Validate target path exists
  if (!targetPath) {
    throw new Error("Route path is required but not provided");
  }

  try {
    // Check if navigating to same base route
    const currentBaseRoute = route.path.split("/")[1];
    const targetBaseRoute = targetPath.split("/")[1];
    const isSameBaseRoute = currentBaseRoute === targetBaseRoute;

    if (isSameBaseRoute) {
      // Navigate home first to force route reload
      await router.push(Constants.enums.ROUTE_ENUM.HOME);
    }

    // Navigate to target route
    await router.push(targetPath);
  } catch (error) {
    throw new Error(`Navigation failed: ${error.message}`);
  }
};

const isMobileSize = () => {
  const breakpoints = useBreakpoints(breakpointsTailwind);
  return breakpoints.smallerOrEqual("md").value;
};

// Tailwind breakpoint helpers (VueUse breakpointsTailwind)
// Examples:
// - Helper.isScreenSmallerThan("lg")  -> < lg
// - Helper.isScreenSmallerOrEqual("md") -> <= md
// - Helper.isScreenGreaterOrEqual("xl") -> >= xl
const isScreenSmallerThan = (breakpoint) => {
  const breakpoints = useBreakpoints(breakpointsTailwind);
  return breakpoints.smaller(breakpoint).value;
};

const isScreenSmallerOrEqual = (breakpoint) => {
  const breakpoints = useBreakpoints(breakpointsTailwind);
  return breakpoints.smallerOrEqual(breakpoint).value;
};

const isScreenGreaterOrEqual = (breakpoint) => {
  const breakpoints = useBreakpoints(breakpointsTailwind);
  return breakpoints.greaterOrEqual(breakpoint).value;
};

// Reactive-ref breakpoint helpers (VueUse breakpointsTailwind).
// Unlike the boolean snapshot helpers above (meant for `Helper.isMobileSize()`
// style calls in templates that re-run every render), these return the reactive
// ref so you can hold it in a `const` and read `.value` in computeds/watchers:
//   const isDesktop = Helper.useIsScreenGreaterOrEqual("lg"); // >= 1024px
//   const isSmUp = Helper.useIsScreenGreaterOrEqual("sm");    // >= 640px
// Call these once at `<script setup>` top level (component scope) so VueUse can
// dispose the media-query listener when the component unmounts.
const useIsScreenGreaterOrEqual = (breakpoint) =>
  useBreakpoints(breakpointsTailwind).greaterOrEqual(breakpoint);

const useIsScreenSmallerOrEqual = (breakpoint) =>
  useBreakpoints(breakpointsTailwind).smallerOrEqual(breakpoint);

const useIsScreenSmallerThan = (breakpoint) =>
  useBreakpoints(breakpointsTailwind).smaller(breakpoint);

const isObject = (value) => {
  return value !== null && !Array.isArray(value) && typeof value === "object";
};

const getRawData = (data) => {
  return isReactive(data) ? toRaw(data) : data;
};

const toDeepRaw = (data) => {
  const rawData = getRawData(data);

  for (const key in rawData) {
    const value = rawData[key];

    if (!isObject(value) && !Array.isArray(value)) {
      continue;
    }

    rawData[key] = toDeepRaw(value);
  }

  return rawData; // much better: structuredClone(rawData)
};

// Helper functions for rxDB
const getCollectionConfig = (customConfig = {}) => {
  const config = {
    options: {
      timestamps: true
    },
    autoMigrate: true,
    ...customConfig
  };
  return config;
};

// Convert migration strategies config to executable functions
const getMigrationStrategies = (config) => {
  if (!isValueExist(config.migration_strategies)) {
    return null;
  }

  return Object.fromEntries(
    Object.entries(config.migration_strategies).map(([key, fnStr]) => [
      key,
      new Function("oldDoc", `return (${fnStr})(oldDoc)`)
    ])
  );
};

/**
 * Check if a given latitude and longitude point is within CMA-506 area
 * @param {number} latitude - The latitude coordinate
 * @param {number} longitude - The longitude coordinate
 * @returns {boolean} - True if the point is within CMA-506, false otherwise
 */
function isPointsPolygon(latitude, longitude, polygon) {
  try {
    // Create a point from the given coordinates
    const point = turf.point([longitude, latitude]);

    // Create a polygon from coordinates
    const polygonGeometry = turf.polygon(polygon);

    // Check if the point is inside the polygon
    return turf.booleanPointInPolygon(point, polygonGeometry);
  } catch (error) {
    console.error("Error checking if point is in polygon:", error);
    return false;
  }
}

const saveToCSV = (columns, data, filename) => {
  const csvContent =
    columns.map((col) => col.title).join(",") +
    "\n" +
    data
      .map((item) => columns.map((col) => item[col.name]).join(","))
      .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
};

const getDistanceBetweenTwoLocations = async (origin, destination) => {
  await window.loadGoogleMapsAPI();
  const service = new google.maps.DistanceMatrixService(); // instantiate Distance Matrix service
  const matrixOptions = {
    origins: [`${origin.lat},${origin.lng}`], // technician locations
    destinations: [`${destination.lat},${destination.lng}`], // customer address
    travelMode: google.maps.TravelMode.DRIVING,
    unitSystem: google.maps.UnitSystem.IMPERIAL
  };

  try {
    // Call Distance Matrix service and wait for response
    const response = await new Promise((resolve, reject) => {
      service.getDistanceMatrix(matrixOptions, (response, status) => {
        if (status === "OK") {
          resolve(response);
        } else {
          reject(new Error("Error with distance matrix"));
        }
      });
    });

    // Get shortest distance value from response matrix
    const distances = response.rows[0].elements.map(
      (element) => element.distance.value
    );
    const shortestDistance = Math.min(...distances);
    return shortestDistance;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getCordsFromGoogleMaps = async (highway) => {
  await window.loadGoogleMapsAPI();
  const directionsService = new google.maps.DirectionsService();

  try {
    const result = await new Promise((resolve, reject) => {
      directionsService.route(
        {
          origin: { lat: highway.start_lat, lng: highway.start_long },
          destination: { lat: highway.end_lat, lng: highway.end_long },
          travelMode: google.maps.TravelMode.DRIVING
        },
        (result, status) => {
          if (status === "OK") {
            resolve(result);
          } else {
            reject(`Directions request failed due to ${status}`);
          }
        }
      );
    });

    console.log("RESULT :", result);
    const points = result.routes[0].overview_path;
    console.log("GPS Points:", points);
    return points;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const findGPSPointsBetweenTwoLocations = async (intervalDistance = 200) => {
  console.log(
    "FINDING GPS POINTS BETWEEN TWO LOCATIONS :",
    highwayInventory.length
  );
  for (const [index, highway] of highwayInventory.entries()) {
    let _points = await getCordsFromGoogleMaps(highway);

    // save original result to csv file as backup
    saveToCSV(
      [
        { name: "lat", title: "lat" },
        { name: "lng", title: "lng" },
        { name: "highway", title: "highway" },
        { name: "highway_id", title: "highway_id" },
        { name: "area_id", title: "area_id" }
      ],
      _points.map((point) => ({
        lat: point.lat(),
        lng: point.lng(),
        highway: highway.label,
        highway_id: highway.id,
        area_id: highway.area_id
      })),
      `google_maps_points_${highway.id}_${highway.label}`
    );

    // now generate points between two locations
    // Convert the points to the format expected by turf.lineString: [longitude, latitude]
    const coordinates = _points.map((point) => [point.lng(), point.lat()]);

    const line = turf.lineString(coordinates);

    const totalDistance = turf.length(line, { units: "meters" });
    const points = [];

    for (let dist = 0; dist <= totalDistance; dist += intervalDistance) {
      const pt = turf.along(line, dist, { units: "meters" });
      points.push(pt.geometry.coordinates); // [lng, lat]
    }

    const _pointsWithOffsets = [];
    for (const point of points) {
      const _start_offset = await getDistanceBetweenTwoLocations(
        { lat: point[1], lng: point[0] },
        { lat: highway.start_lat, lng: highway.start_long }
      );
      const _end_offset = await getDistanceBetweenTwoLocations(
        { lat: point[1], lng: point[0] },
        { lat: highway.end_lat, lng: highway.end_long }
      );
      _pointsWithOffsets.push({
        lat: point[1],
        lng: point[0],
        start_offset: _start_offset,
        end_offset: _end_offset
      });
    }

    // await Helper.getDistanceBetweenTwoLocations({ lat: 55.2942267, lng: -114.8436838 }, { lat: 55.32766277, lng: -114.8993305 });

    // then logic to save this as csv file with title lat long
    const columns = [
      { name: "lat", title: "lat" },
      { name: "lng", title: "lng" },
      { name: "highway", title: "highway" },
      { name: "highway_id", title: "highway_id" },
      { name: "area_id", title: "area_id" },
      { name: "start_offset", title: "start_offset" },
      { name: "end_offset", title: "end_offset" }
    ];
    saveToCSV(
      columns,
      _pointsWithOffsets.map((point) => ({
        lat: point.lat,
        lng: point.lng,
        highway: highway.label,
        highway_id: highway.id,
        area_id: highway.area_id,
        start_offset: point.start_offset,
        end_offset: point.end_offset
      })),
      `points_${highway.id}_${highway.label}_${intervalDistance}m`
    );
  }
  console.log("FINISHED FINDING GPS POINTS BETWEEN TWO LOCATIONS");
};

const parseHighwayVirtualPointsData = () => {
  const result = Papa.parse(csvRaw, {
    header: true,
    skipEmptyLines: true
  });
  return result.data;
};

const findPointsWithinRadius = (centerLat, centerLng, radiusMeters = 500) => {
  try {
    // Parse the CSV data if not provided
    const parsedData = parseHighwayVirtualPointsData();

    if (!parsedData || !Array.isArray(parsedData)) {
      console.error("No valid data found");
      return [];
    }

    // Create a point for the center location
    const centerPoint = turf.point([centerLng, centerLat]);

    const pointsWithinRadius = [];

    for (const point of parsedData) {
      // Skip if lat/lng are missing or invalid
      if (!point.lat || !point.lng) continue;

      // Convert string coordinates to numbers
      const lat = parseFloat(point.lat);
      const lng = parseFloat(point.lng);

      if (isNaN(lat) || isNaN(lng)) continue;

      // Create a point for the current data point
      const dataPoint = turf.point([lng, lat]);

      // Calculate distance between center and data point
      const distance = turf.distance(centerPoint, dataPoint, {
        units: "meters"
      });

      // Check if point is within the specified radius
      if (distance <= radiusMeters) {
        pointsWithinRadius.push({
          ...point,
          distance_from_center: Math.round(distance), // Add distance info
          lat: lat,
          lng: lng
        });
      }
    }

    // Sort by distance (closest first)
    pointsWithinRadius.sort(
      (a, b) => a.distance_from_center - b.distance_from_center
    );

    console.log(
      `Found ${pointsWithinRadius.length} points within ${radiusMeters}m of (${centerLat}, ${centerLng})`
    );

    return pointsWithinRadius;
  } catch (error) {
    console.error("Error finding points within radius:", error);
    return [];
  }
};

/**
 * Process page validation settings from template settings
 * @param {Object} templateSettings - The template settings object
 * @param {string} pageId - The current page ID
 * @param {Object} formData - The current form data
 * @returns {Object} Validation result with isValid and invalidFields
 */
function processPageValidation(templateSettings, pageId, formData) {
  try {
    if (!templateSettings || !templateSettings.pageValdiation) {
      return { isValid: true, invalidFields: [] };
    }

    const pageValidation = templateSettings.pageValdiation[pageId];
    if (!pageValidation || !Array.isArray(pageValidation)) {
      return { isValid: true, invalidFields: [] };
    }

    let isValid = true;
    const invalidFields = [];

    // Process each validator for the page
    for (const validator of pageValidation) {
      if (!validator.fields || !Array.isArray(validator.fields)) {
        continue;
      }

      // If there's a validation condition, evaluate it
      if (validator.validationCondition) {
        const conditionResult = evaluateTriggerConditions(
          validator.validationCondition,
          formData
        );

        // If condition fails, mark all fields in this validator as invalid
        if (!conditionResult) {
          isValid = false;
          invalidFields.push(...validator.fields);
        }
      }
    }

    return { isValid, invalidFields };
  } catch (error) {
    console.error("Error processing page validation:", error);
    return { isValid: true, invalidFields: [] };
  }
}

function convertObjectToArray(obj) {
  if (!obj || Array.isArray(obj)) {
    return obj;
  }
  const keys = Object.keys(obj).sort((a, b) => parseInt(a) - parseInt(b));
  return keys.map((key) => obj[key]);
}

const simpliFiedGeoData = (points, tolerance = 0.01) => {
  // Convert array of {lat,lng} points to GeoJSON LineString coordinates
  const coordinates = points.map((point) => [point.lng, point.lat]);

  // Create GeoJSON LineString
  const geojson = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: coordinates
    }
  };

  console.log("BEFORE SIMPLIFYING:", coordinates.length);

  // Simplify the LineString
  const simplified = turf.simplify(geojson, {
    tolerance: tolerance,
    highQuality: true
  });

  console.log("AFTER SIMPLIFYING:", simplified.geometry.coordinates.length);

  // Convert back to array of {lat,lng}
  const simplifiedPoints = simplified.geometry.coordinates.map((coord) => ({
    lat: coord[1],
    lng: coord[0]
  }));

  return simplifiedPoints;
};

// Function to get all IndexedDB table names
const getAllIndexedDBTables = async () => {
  try {
    // Get list of all databases
    const databases = await window.indexedDB.databases();
    const tables = [];

    // Iterate through each database
    for (const db of databases) {
      // Open database connection
      const dbConnection = await new Promise((resolve, reject) => {
        const request = window.indexedDB.open(db.name);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      });

      // Get object store names (tables)
      const objectStoreNames = Array.from(dbConnection.objectStoreNames);
      tables.push({
        database: db.name,
        tables: objectStoreNames
      });

      // Close connection
      dbConnection.close();
    }

    return tables;
  } catch (error) {
    console.error("Error getting IndexedDB tables:", error);
    return [];
  }
};

const getAppURL = () => {
  return import.meta.env.VITE_APP_URL;
};

// will be used to evaluate the formula
// will return the value, it'll not set value to the field
function evaluateFormula(formula, response, options = {}) {
  try {
    const {
      isMultiple = false,
      multipleParentId = null,
      recordIndex = 0,
      decimalPlaces = 2
    } = options;

    const regex = /\$\(([^)]+)\)(?:\.(\w[\w-]*))?/g;
    let total = 0;

    const replacedFormula = formula.replace(regex, (match, fieldId, key) => {
      let val;
      if (isMultiple) {
        const parentVal = findAndSetKeyInObject(response, multipleParentId);
        val = parentVal?.[recordIndex]?.[fieldId] ?? 0;
      } else {
        val = findAndSetKeyInObject(response, fieldId);
      }

      if (key) {
        if (Array.isArray(val)) {
          total = val.reduce((sum, item) => {
            const keyVal = item[key];
            return sum + (!isNaN(parseFloat(keyVal)) ? parseFloat(keyVal) : 0);
          }, 0);
          val = total;
        } else if (typeof val === "object" && val !== null && key in val) {
          val = val[key];
        } else {
          return "0";
        }
      }
      return !isNaN(val) ? (val ? val.toFixed(decimalPlaces) : "0.00") : "0.00";
    });

    if (!replacedFormula) return 0.0;
    const result = eval(replacedFormula);
    return isNaN(result) ? 0.0 : result.toFixed(decimalPlaces);
  } catch (error) {
    console.error("Formula evaluation error:", error);
    return 0.0;
  }
}

async function handleAddMoreMenu(params) {
  const {
    config,
    addMoreMenuObj,
    response,
    formTemplateData,
    getFormId,
    saveFormResponse = true,
    router
  } = params;
  async function fetchFormTemplateById() {
    try {
      const formTemplateStore = useFormTemplateStore();
      const formPageStore = useFormPageStore();
      let _res = await formTemplateStore.getFormTemplateById(
        params.config.template_id,
        true
      );
      if (_res.error) throw _res.error;
      // reset the page from list
      // Setting up the template data with no value
      for (const element of _res.data.pages) {
        if (element.page_id) {
          // if not list and have page_id then pull the pageObj from the server
          const _pageRes = await formPageStore.getFormPageById(
            element.page_id,
            true
          );
          if (_pageRes.error) throw _pageRes.error;
          element.pageObj = _pageRes.data;
        }
      }
      // returning data to the caller
      return _res.data.pages && _res.data.pages.length > 0
        ? _res.data.pages[0]
        : null;
    } catch (error) {
      console.error("Error opening add more menu:", error);
    }
  }

  const navOnCancel = async () => {
    if (params.config.navigate_to_on_cancel) {
      router.replace(params.config.navigate_to_on_cancel);
    }
  };

  const onAddMoreMenuOptionSave = async (options = {}) => {
    try {
      // Generate template response from page object
      const templateResponse = generateFormTemplateResponse({
        id: null,
        pages: [addMoreMenuObj.value.pageObj]
      });

      // Deep copy to avoid mutations
      const fieldValues = deepCopy(templateResponse);

      // Find and set key in object
      let record = findAndSetKeyInObject(
        fieldValues,
        addMoreMenuObj.value.pageObj.id
      );

      // Add unique ID to record
      record = {
        ...record,
        id: getRandomUuid()
      };
      response.value[getFormId][addMoreMenuObj.value.pageObj.id] = record;

      // Process auto-increment if configured
      if (params.config.auto_increment_fields && !options.setup) {
        await processAddMoreMenuAutoIncrement({
          formTemplateData,
          response: response.value,
          templateId: getFormId,
          autoIncrementFields: params.config.auto_increment_fields
        });
      }
    } catch (error) {
      console.error("Error opening add more menu:", error);
    }

    if (params.config.set_data && !options.setup) {
      findAndSetKeyInObject(
        response.value,
        params.config.set_data.key,
        params.config.set_data.value
      );
    }

    if (params.config.saveFormResponse !== false) {
      await saveFormResponse();
    }

    // Skip navigation if this is a setup call
    if (options.setup) {
      return;
    }

    // check if setting has any value else go with default navigation

    // default route
    if (params.config.navigate_to) {
      router.replace(params.config.navigate_to);
    }
  };
  // Check if addMoreMenuObj is provided in params
  if (params.addMoreMenuObj) {
    try {
      params.addMoreMenuObj.value.pageObj = await fetchFormTemplateById();
      params.addMoreMenuObj.value.isVisible = true;
      params.addMoreMenuObj.value.title = config?.title || "Add New";
      params.addMoreMenuObj.value.onSaveClick =
        onAddMoreMenuOptionSave || (() => {});
      params.addMoreMenuObj.value.onCancelClick = params.config
        .navigate_to_on_cancel
        ? navOnCancel
        : () => {};
    } catch (error) {
      // If errorObj is provided, show error
      if (params.errorObj) {
        params.errorObj.value.isVisible = true;
        params.errorObj.value.errorMsg = error;
      } else {
        console.error("Error opening add more menu:", error);
      }
    }
  } else {
    console.warn(
      "addMoreMenuObj or fetchFormTemplateById not provided in params"
    );
  }
}

function getHighwayDisplayName(highway, highwayDisplayData) {
  if (!highway) return "";

  // First priority: highwayDisplayData.road
  if (highwayDisplayData?.road && highwayDisplayData.road !== "null") {
    return highwayDisplayData.road;
  }

  // Second priority: highway.value.road (for newer forms)
  if (highway.value.road && highway.value.road !== "null") {
    return highway.value.road;
  }

  // Fallback: highway.value.label (for older forms)
  if (highway.value.label && highway.value.label !== "null") {
    return highway.value.label;
  }

  return "No Road";
}

const isWifiOnlyMode = () => {
  // get query params
  const queryParams = getQueryPairs();
  return (
    queryParams.use_mode?.toUpperCase() === Constants.enums.USE_MODE.WIFI_ONLY
  );
};

const isCellularOnlyMode = () => {
  const queryParams = getQueryPairs();
  return (
    queryParams.use_mode?.toUpperCase() ===
    Constants.enums.USE_MODE.CELLULAR_ONLY
  );
};

const isWifiAndCellularMode = () => {
  const queryParams = getQueryPairs();
  return (
    queryParams.use_mode?.toUpperCase() ===
    Constants.enums.USE_MODE.WIFI_AND_CELLULAR
  );
};

// convert arrow notation to dot notation
// example: response->62a714d5->1b4c0bc3->>15376b7e -> response.62a714d5.1b4c0bc3.15376b7e
const convertArrowToDotNotation = (arrowPath) => {
  if (!arrowPath || typeof arrowPath !== "string") return arrowPath;

  return arrowPath.replace(/->>/g, ".").replace(/->/g, ".");
};

async function getMaxBladeTransferQuantity(shopId, bladeTypeId) {
  try {
    const bladesStore = useBladesStore();

    const res = await bladesStore.getBladesInventory({
      queryParams: [
        {
          field: "shop_id",
          value: shopId
        },
        {
          field: "blade_type_id",
          value: bladeTypeId
        }
      ]
    });

    return res?.data?.[0]?.qty_on_hand || 0;
  } catch (error) {
    console.error("Error getting max blade quantity:", error);
    return 0;
  }
}

const formatCamelCase = (str) => {
  if (!str) return "N/A";
  // Convert camelCase to readable format
  // Insert space before uppercase letters and capitalize first letter
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
};

// Ranking helpers for blade-change display order. The order itself lives in
// Constants.enums.BLADE_CHANGE_ORDER so it stays in sync with the mobile form:
// sections Wing -> Front -> Under, positions Left -> Center-Left -> Center ->
// Center-Right -> Right -> Blade Saver.
const bladeSectionRank = (section) =>
  Constants.enums.BLADE_CHANGE_ORDER.SECTIONS[(section || "").toUpperCase()] ??
  99;

// Normalize a raw blade_position ("frontCenterLeft", "front_center_left", …)
// into a canonical snake_case lateral token ("center_left") so it can be
// ranked regardless of how it was stored. The section prefix, when present, is
// stripped first because the lateral order is the same across sections.
const bladePositionRank = (section, position) => {
  if (!position) return 99;
  let lateral = position;
  const sec = (section || "").toLowerCase();
  if (sec && lateral.toLowerCase().startsWith(sec)) {
    lateral = lateral.slice(sec.length);
  }
  const token = lateral
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/^_/, "")
    .toLowerCase();
  const idx = Constants.enums.BLADE_CHANGE_ORDER.POSITIONS.indexOf(token);
  return idx === -1 ? 99 : idx;
};

// Group per-blade rows by section + position so a double- (or multi-) bladed
// position becomes a single merged row listing all of its blades, sorted in the
// same order as the mobile blade-change form. Each returned group is
// { blade_section, blade_position, created_at (earliest in group), blades: [{ id, description }] }.
const groupBladeChangesByPosition = (list) => {
  if (!Array.isArray(list)) return [];
  const groups = {};
  for (const row of list) {
    const key = `${row.blade_section}|${row.blade_position}`;
    if (!groups[key]) {
      groups[key] = {
        blade_section: row.blade_section,
        blade_position: row.blade_position,
        created_at: row.created_at,
        blades: []
      };
    }
    groups[key].blades.push({
      id: row.blade_type_id,
      description: row.blade_type_description
    });
    // Keep the earliest created_at for the group.
    if (
      row.created_at &&
      (!groups[key].created_at ||
        new Date(row.created_at) < new Date(groups[key].created_at))
    ) {
      groups[key].created_at = row.created_at;
    }
  }
  return Object.values(groups).sort((a, b) => {
    const section =
      bladeSectionRank(a.blade_section) - bladeSectionRank(b.blade_section);
    if (section !== 0) return section;
    return (
      bladePositionRank(a.blade_section, a.blade_position) -
      bladePositionRank(b.blade_section, b.blade_position)
    );
  });
};

const exportToExcel = async (data, file_name_prefix, sort = null) => {
  if (!data || data.length === 0) {
    console.error("No data to export");
    return;
  }

  const workbook = new ExcelJs.Workbook();
  workbook.creator = "Phoenix App";
  workbook.created = new Date();

  // Get column headers from first row
  const headers = Object.keys(data[0]);

  // Sort data if sort is provided
  let sortedData = [...data];
  if (sort && sort.sortField) {
    sortedData.sort((a, b) => {
      const aVal = a[sort.sortField];
      const bVal = b[sort.sortField];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sort.sortOrder === 1 ? comparison : -comparison;
    });
  }

  // Create worksheet
  const worksheet = workbook.addWorksheet("Report Data");

  // Set column headers
  worksheet.columns = headers.map((header) => ({
    header: header,
    key: header,
    width: 20
  }));

  // Add header row with styling
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" }
  };

  // Add data rows
  sortedData.forEach((row) => {
    worksheet.addRow(row);
  });

  // Format date/time for filename
  const dateTimeFormatted = convertToLocalDate(
    new Date(),
    "Do MMM, YYYY h:mm A"
  );
  const fileName = file_name_prefix
    ? `${file_name_prefix}${dateTimeFormatted}.xlsx`
    : `Report-${dateTimeFormatted}.xlsx`;

  // Generate and download file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return workbook;
};

const formatNumberWithCommas = (value, decimalPlaces = 2) => {
  // Convert to number first (in case it's string)
  const num = Number(value);
  if (isNaN(num)) return "0.00";
  // toLocaleString handles commas and decimal places
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
};

const calculateDateRange = (dateRange, customDateRange = null) => {
  const today = moment();
  switch (dateRange) {
    case "current_year":
      return {
        from: today.clone().startOf("year").toISOString(),
        to: today.clone().endOf("year").toISOString()
      };
    // Like-for-like "to-date" windows for KPI trend comparisons: compare the
    // elapsed portion of the current period against the same elapsed portion of
    // the previous period (e.g. Sep 1–19 vs Aug 1–19), avoiding partial-vs-full
    // skew while the current month/week is still in progress.
    case "month_to_date":
      return {
        from: today.clone().startOf("month").toISOString(),
        to: today.clone().toISOString()
      };
    case "prev_month_to_date":
      return {
        from: today.clone().subtract(1, "month").startOf("month").toISOString(),
        to: today.clone().subtract(1, "month").toISOString()
      };
    case "week_to_date":
      return {
        from: today.clone().startOf("week").toISOString(),
        to: today.clone().toISOString()
      };
    case "prev_week_to_date":
      return {
        from: today.clone().subtract(1, "week").startOf("week").toISOString(),
        to: today.clone().subtract(1, "week").toISOString()
      };
    case "current_week":
      return {
        from: today.clone().startOf("week").toISOString(),
        to: today.clone().endOf("week").toISOString()
      };
    case "last_week":
      return {
        from: today.clone().subtract(1, "week").startOf("week").toISOString(),
        to: today.clone().subtract(1, "week").endOf("week").toISOString()
      };
    case "current_month":
      return {
        from: today.clone().startOf("month").toISOString(),
        to: today.clone().endOf("month").toISOString()
      };
    case "last_month":
      return {
        from: today.clone().subtract(1, "month").startOf("month").toISOString(),
        to: today.clone().subtract(1, "month").endOf("month").toISOString()
      };
    case "custom_range":
      if (customDateRange && customDateRange.filter((x) => x).length > 1) {
        return {
          from: moment(customDateRange[0]).startOf("day").toISOString(),
          to: moment(customDateRange[1]).endOf("day").toISOString()
        };
      } else if (customDateRange) {
        return {
          from: moment(customDateRange[0]).startOf("day").toISOString(),
          to: moment(customDateRange[0]).endOf("day").toISOString()
        };
      } else {
        return null;
      }
    default:
      return null;
  }
};

/**
 * Returns the CSS class string for a Weekly Patrol Report status cell.
 * Uses Constants.enums.PATROL_REPORT_STATUS_CLASSES (defined in enums).
 */
function getPatrolReportStatusClass(status, isScheduledForDay = true) {
  const C = Constants.enums.PATROL_REPORT_STATUS_CLASSES;
  if (status === null) return C.empty;
  if (!status) return C.emptyGray;

  switch (status) {
    case Constants.enums.QC_STATUS_OPTIONS.PATROLLED:
      return isScheduledForDay ? C.patrolledScheduled : C.patrolledNotScheduled;
    case Constants.enums.QC_STATUS_OPTIONS.QC_PASS:
      return isScheduledForDay ? C.patrolledScheduled : C.patrolledNotScheduled;
    case Constants.enums.QC_STATUS_OPTIONS.QC_CHECK:
      return isScheduledForDay ? C.qcCheckScheduled : C.qcCheckNotScheduled;
    case Constants.enums.QC_STATUS_OPTIONS.QC_FAIL:
      return isScheduledForDay ? C.qcFailScheduled : C.qcFailNotScheduled;
    case Constants.enums.QC_STATUS_OPTIONS.NON_PATROL:
      return C.nonPatrol;
    case Constants.enums.QC_STATUS_OPTIONS.SCHEDULED:
      return C.scheduled;
    default:
      return C.default;
  }
}

// Tailwind class string for a task risk-level pill (LOW / MED / HIGH).
// Used by the FLHA task summary and submitted views.
function getRiskPillClass(level) {
  const v = (level || "").toString().toUpperCase();
  if (v === "HIGH") {
    return "bg-red-100 text-red-800 border-red-300";
  }
  if (v === "MED" || v === "MEDIUM") {
    return "bg-orange-100 text-orange-800 border-orange-300";
  }
  return "bg-green-100 text-green-800 border-green-300";
}

function mapDatabaseErrorsToUserMessages(error) {
  //The constraint name comes in the message. Needs to be extracted.
  //Find the words starting where it says "constraint"
  const constraintMatch = error.message.match(/constraint "([^"]+)"/);
  //The constraint should be the word after "constraint" in position 1
  const constraint = constraintMatch?.[1];

  if (constraint) {
    return (
      Constants.enums.DB_ERRORS?.[error.code]?.[constraint] ?? error.message
    );
  }

  return error.message;
}

function dateIsAfterToday(compareDate) {
  const today = new Date();
  // clear the times to only compare on the day itself to avoid any issues with racing, or other time specific issues,
  // comparing on the day prevents data from being entered in future weeks as well (in the case of PatrolWeeklyReportDashboard usage)
  today.setHours(0, 0, 0, 0);
  compareDate.setHours(0, 0, 0, 0);
  return today.getTime() < compareDate.getTime();
}

export {
  exportToCsv,
  convertImageToBase64,
  showToast,
  getStatusName,
  evaluateTriggerConditions,
  deepMerge,
  checkEmailVersion,
  findAndSetKeyInObject,
  findObjectByIdAndSetKey,
  getValueByPath,
  toDeepRaw,
  getRawData,
  isObject,
  getPatrolReportStatusClass,
  getRiskPillClass,
  preprocessPdfLayoutTextValues,
  resolveSignatureRefs,
  hasSignature,
  resolveSignatureSrc
};
export default {
  exportToCsv,
  compressAndRotateFile,
  downloadToLocal,
  isMobileBrowser,
  generateFLRAEmailBody,
  automatedMessagePlaceHolder,
  getShippingReqUrl,
  resetPiniaStore,
  parseHashUrl,
  showToast,
  convertToLocalDateTime,
  convertToLocalTime,
  convertToLocalTime24,
  convertToLocalDate,
  unixToTimeStamp,
  isSessionExpiringSoon,
  getTokenAge,
  getManagerEmail,
  deepCopy,
  replaceTokensDeep,
  isValueExist,
  isStringValueExist,
  blankToNull,
  stripHtml,
  formDataValidation,
  base64ToBlobUrl,
  createBlobUrlFromBuffer,
  getBase64FromBlob,
  createBase64FromBuffer,
  getCurrentVersion,
  getEmpId,
  getInitials,
  getUserName,
  getUserEmail,
  getCurrentUserId,
  getCurrentUserEmail,
  isCurrentEnvironmentProd,
  getCurrentEnvironment,
  isCurrentEnvironmentDev,
  isCurrentEnvironmentStaging,
  getHseEmail,
  isUserSubmitter,
  isUserAdmin,
  isUserSuperAdmin,
  isUserShippingReqAdmin,
  isContainsAlphabet,
  getStatusColor,
  getQCProgressStatusColor,
  getDummyObj,
  isImage,
  isPdf,
  isHTML,
  openLinkInNewTab,
  formatTimestamp,
  getBaseUrl,
  objToStringify,
  getRandomUuid,
  isNativePlatForm,
  convertImageToBase64,
  getRemoteStoragePrefix,
  getUserFlraAttachmentsBucket,
  getSupabaseFlraAttachmentsBucket,
  getUsersFormTemplatesAttachmentsBucket,
  getSupabaseFormTemplatesAttachmentsBucket,
  getSupabaseStorageBucket,
  getIndexedDBVersion,
  getCurrentUserFullName,
  getMediaTypeFromBase64,
  getExtensionFromBase64,
  getAttachmentSizeInMB,
  getBoldTxt,
  generateFormTemplateResponse,
  populateListTemplateFromResponse,
  processFieldValues,
  calculateFilledPercentage,
  findAndSetKeyInObject,
  findObjectByIdAndSetKey,
  getValueByPath,
  negateInvoiceFields,
  sanitizeFilename,
  getFileExtension,
  deepMerge,
  isUserSupervisor,
  getStatusName,
  copyWithNullValues,
  clearAllSiteData,
  rotateImage90Degrees,
  resolveRecipient,
  resolveRecipients,
  resolveRoleRecipients,
  sendEmail,
  buildAndSendEmail,
  evaluateTriggerConditions,
  getCurrentUserOid,
  prepareEmailData,
  convertGroupIdsToEmails,
  processEmailTriggers,
  compareVersions,
  convertEnumToSelectOptions,
  formatEnumLabel,
  getEnumValueLabel,
  formatJsonForDisplay,
  extractUniqueValuesAsOptions,
  base64ToFile,
  isValidEmail,
  findDynamicKeyInExpression,
  flattenFormFields,
  loadFormPages,
  getCreditInvoiceMessage,
  getCreditAndRebillInvoiceMessage,
  extractUniqueValuesAsOptions,
  getDateRangeFromPreset,
  generateFormFileName,
  checkEmailVersion,
  evaluateTriggers,
  extractFromJsonPath,
  generatePDF,
  getPdfLayout,
  openSendEmailDialog,
  onSendEmailSuccess,
  evaluateClickButtonTriggers,
  saveFormResponse,
  handleEmailVersionCheck,
  beforeAfterActionsHandler,
  onNavigationBtnClick,
  generateAutoIncrementValue,
  getDefaultDateValue,
  consoleSuccess,
  getCurrentLocationLatLong,
  handleDashboardAction,
  getQueryPairs,
  getHiddenFilterFields,
  isFieldHidden,
  handleRouteNavigation,
  isMobileSize,
  isScreenSmallerThan,
  isScreenSmallerOrEqual,
  isScreenGreaterOrEqual,
  useIsScreenGreaterOrEqual,
  useIsScreenSmallerOrEqual,
  useIsScreenSmallerThan,
  getCollectionConfig,
  getMigrationStrategies,
  isPointsPolygon,
  findGPSPointsBetweenTwoLocations,
  getDistanceBetweenTwoLocations,
  parseHighwayVirtualPointsData,
  findPointsWithinRadius,
  getDefectsProgressStatusColor,
  processPageValidation,
  processAddMoreMenuAutoIncrement,
  simpliFiedGeoData,
  getStatusClasses,
  getStatusClassesReversed,
  getDefectsStatusIconClass,
  setValueByPath,
  getValueByPath,
  convertObjectToArray,
  getAllIndexedDBTables,
  getStatusGroupClasses,
  getStatusGroupProgressColor,
  getGroupByKey,
  getGroupByStatus,
  getStatusGroupTooltip,
  getStatusItemsStructure,
  getEmergencyStatusItemsStructure,
  getStatusGroups,
  getFilterOptions,
  getAppURL,
  evaluateFormula,
  getHighwayDisplayName,
  isWifiOnlyMode,
  isCellularOnlyMode,
  isWifiAndCellularMode,
  convertArrowToDotNotation,
  getMaxBladeTransferQuantity,
  formatCamelCase,
  groupBladeChangesByPosition,
  exportToExcel,
  formatNumberWithCommas,
  calculateDateRange,
  convertToDisplayableImageFile,
  convertSvgToRasterFile,
  convertSvgUrlToRasterUrl,
  isDisplayableImageType,
  isNonDisplayableImageFile,
  getPatrolReportStatusClass,
  getRiskPillClass,
  resolveSignatureRefs,
  hasSignature,
  resolveSignatureSrc,
  mapDatabaseErrorsToUserMessages,
  dateIsAfterToday
};
